import { Controller } from "@hotwired/stimulus"
import { readCaptureMetadata } from "../lib/exif_reader"
import { UPLOAD_LIMIT_BYTES, showSizeLimitModal } from "../lib/size_limit_modal"
import { showToast } from "../lib/toast"
import { uploadAllowed, uploadExtension } from "../lib/upload_extensions"
import { FormFieldRendererMixin, TemplateEditorMixin, applyMixin } from "../lib/form_template_editor"

// One presigned PUT carries a video up to 500MB (PresignableUploads); past
// that it goes in parts through Photos' big-video upload (K9-6).
const SINGLE_PUT_VIDEO_MAX = 500 * 1024 * 1024
const VIDEO_EXTENSIONS = ["mp4", "mov", "webm"]

class ReportFormController extends Controller {
  static targets = [
    "reportType", "reportNumber",
    "workforceSection", "workforceRows", "workforceDirectTotal", "workforceIndirectTotal",
    "equipmentSection", "equipmentRows", "equipmentOwnTotal", "equipmentThirdPartyTotal",
    // Upload targets (single drop zone, split display)
    "dropzone", "fileInput", "photoList", "fileList", "hiddenFields",
    "summarizeWrap", "summarizeStatus", "aiSummaryStatus",
    // Action bar
    "draftBtn", "publishBtn", "publishDropdownToggle", "publishDropdownMenu", "uploadStatus", "uploadStatusText",
    // Form template
    "formTemplateFields",
    // Geolocation
    "latitude", "longitude",
    // Custom forms (APR, etc.)
    "standardFields", "aprFields", "rdoFields", "cdvFields", "ddsFields", "rdmFields", "architectFields",
    // Daily-log-only metrics (Weather Delay + Workers at Jobsite)
    "dailyOnlyFields",
    // "Include automatic weather" switch (types with allow_hide_auto_weather)
    "weatherSwitchWrap", "weatherSwitchInput", "weatherSwitchHidden",
    // Word counter
    "notesField", "wordCount",
    // New report type popup (commander-only)
    "newTypeModal", "newTypeName", "newTypeError", "newTypeSubmit",
    // 22.1 — AI template import (drop zone in the popup + preview modal)
    "newTypeDrop", "newTypeFile", "newTypeAiStatus",
    "templatePreviewModal", "templatePreviewBody", "templatePreviewName",
    "templatePreviewSave", "templatePreviewError", "templateInspector",
    "templatePreviewConflict", "templatePreviewConflictName",
    "sigModal", "sigName", "sigKindTab", "sigTypedPanel", "sigDrawnPanel", "sigPad", "sigTypedPreview", "sigError",
    "sigSelfBlock", "sigOtherPanel", "sigOtherEmail", "sigOtherName", "sigCandidates", "sigConfirmBtn",
    "notesBlock",
    // Photos on a grid row (Larry 2026-09-08): one shared file input
    "rowPhotoInput"
  ]

  static values = {
    designPhase: Boolean,
    descriptionSuggestionsUrl: String,
    workforceSuggestions: Array,
    equipmentSuggestions: Array,
    newWorkforceNameUrl: String,
    newEquipmentNameUrl: String,
    deleteWorkforceNameUrl: String,
    deleteEquipmentNameUrl: String,
    presignUrl: String,
    registerUrl: String,
    summarizeUrl: String,
    pollThumbnailsUrl: String,
    heicWorkerUrl: String,
    existingPhotos: Array,
    existingFiles: Array,
    reportNumbersByType: Object,
    nextReportNumberUrl: String,
    createTypeUrl: String,
    extractTemplateUrl: String,
    builderModule: Boolean,
    formTemplatesByType: Object,
    signerName: String,
    signIconHtml: String,
    signerCandidates: { type: Array, default: [] },
    weatherToggleTypeIds: Array,
    formData: Object,
    projectLatitude: Number,
    projectLongitude: Number,
    locationVerifiedMsg: String,
    locationWarningMsg: String,
    scheduleTasksUrl: String,
    photoScheduleEnabled: String,
    i18nLinkActivity: String,
    i18nSelectActivity: String,
    i18nSearch: String,
    i18nNoMatches: String,
    i18nNoActivities: String,
    i18nCurrent: String,
    i18nAddComment: String,
    autoSummarize: Boolean,
    photoAiUrl: String,
    photoCaptionUrl: String,
    presignMultipartUrl: String,
    completeMultipartUrl: String,
    abortMultipartUrl: String,
    sizeLimitText: Object,
    i18nVideoUploadFailed: String,
    i18nFileTypeNotAllowed: String,
    projectId: Number,
    projectPhase: String
  }

  connect() {
    this._uploadPhotos = []    // { file, comment, convertedBlob, _uploadResult, _heicPromise }
    this._uploadFiles = []     // { file, _uploadResult }
    this._photoIds = []        // photo IDs (existing + new images)
    this._rowPhotos = {}       // grid photos: { tableKey: { rowKey: [photoId] } } (Larry 2026-09-08)
    this._rowFlags = {}        // grid rows flagged "needs attention" for the reviewer: { tableKey: { rowKey: true } }
    this._thumbById = {}       // photoId -> thumbnail url (existing or a fresh upload's preview)
    this._fileIds = []         // photo IDs (existing + new non-image files)
    this._removedIds = []      // photo IDs removed during edit
    this._photoComments = {}   // { photoId: comment }
    this._pendingUploads = 0
    this._eagerUploadPromises = []
    this._disconnected = false
    // A field Studio just approved or undid (2026-10-17): the form redraws itself, no reload.
    this._onStudioTemplate = (e) => this._studioTemplate(e.detail || {})
    window.addEventListener("studio:form-template", this._onStudioTemplate)

    // AI summary state. The summary is inserted INTO the notes field as an
    // "AI Summary: …" block; _aiInsertedText remembers the exact inserted
    // text so a re-run can replace it verbatim. If the notes already carry
    // an AI block from a previous save (edit page), treat it as the user's
    // content — auto runs won't touch it.
    this._summarizing = false
    this._summarizePending = null
    this._aiInsertedText = ""
    this._notesHadAiBlock = this.hasNotesFieldTarget && this.notesFieldTarget.value.includes("AI Summary:")

    // Combined photo summary — composed into the notes' AI block together
    // with the file summaries: file lines first, then one photo line ending
    // "(All Photos Attached)".
    this._photoAiFiles = []
    this._photoSummarizing = false
    this._photoSummaryPending = false
    this._fileSummaryText = ""
    this._photoSummaryText = ""

    this._activeAutocomplete = null // tracks the currently open autocomplete dropdown
    this._boundCloseAutocomplete = this._closeAutocompleteOnOutsideClick.bind(this)
    document.addEventListener("click", this._boundCloseAutocomplete)

    // Photo-schedule integration state
    this._photoTasks = {}       // { photoId: { task_id, percent_complete } }
    this._scheduleTasks = []    // cached task list from server
    this._photoScheduleEnabled = this.photoScheduleEnabledValue === "true"
    this._scheduleTasksPromise = this._photoScheduleEnabled && this.scheduleTasksUrlValue
      ? this._fetchScheduleTasks()
      : Promise.resolve()

    this._initExisting()
    this._initRowPhotos()
    this._renderFormTemplate()
    this._toggleNotes()
    this._checkGeolocation()
    this._toggleCustomForms()
    this.updateWordCount()
    this._initDescriptionAutocompletes()
  }

  disconnect() {
    this._disconnected = true
    window.removeEventListener("studio:form-template", this._onStudioTemplate)
    clearTimeout(this._autoSumTimer)
    clearTimeout(this._sumDoneTimer)
    clearInterval(this._sumTimer)
    clearTimeout(this._photoSumTimer)
    clearTimeout(this._photoSumDoneTimer)
    clearInterval(this._photoSumTicker)
    document.removeEventListener("click", this._boundCloseAutocomplete)
    if (this._heicWorker) {
      this._heicWorker.terminate()
      this._heicWorker = null
      this._heicCallbacks = {}
    }
  }

  // ── Validation (same pattern as project wizard) ────────────────────

  async validateAndSubmit(e) {
    e.preventDefault()

    // Wait for any in-flight uploads to finish
    if (this._pendingUploads > 0) {
      await Promise.allSettled(this._eagerUploadPromises)
      this._syncHiddenFields()
    }

    const isApr = this.hasAprFieldsTarget && !this.aprFieldsTarget.classList.contains("hidden")
    const isRdo = this.hasRdoFieldsTarget && !this.rdoFieldsTarget.classList.contains("hidden")
    const isCdv = this.hasCdvFieldsTarget && !this.cdvFieldsTarget.classList.contains("hidden")
    const isDds = this.hasDdsFieldsTarget && !this.ddsFieldsTarget.classList.contains("hidden")
    const isRdm = this.hasRdmFieldsTarget && !this.rdmFieldsTarget.classList.contains("hidden")
    const isArchitect = this.hasArchitectFieldsTarget && !this.architectFieldsTarget.classList.contains("hidden")

    // Only validate visible fields (skip standard fields when a custom type is active)
    const scope = isApr ? this.aprFieldsTarget : isRdo ? this.rdoFieldsTarget : isCdv ? this.cdvFieldsTarget : isDds ? this.ddsFieldsTarget : isRdm ? this.rdmFieldsTarget : isArchitect ? this.architectFieldsTarget : this.element
    const fields = [...scope.querySelectorAll("[data-required]")].filter(f => f.offsetParent !== null)
    let valid = true

    fields.forEach(field => {
      this._clearFieldError(field)

      const val = field.value.trim()
      if (!val) {
        this._showFieldError(field, field.dataset.errorText || "This field is required")
        valid = false
        return
      }

      const minWords = field.dataset.minWords
      if (minWords && val.split(/\s+/).length < parseInt(minWords)) {
        this._showFieldError(field, `Minimum of ${minWords} words`)
        valid = false
      }
    })

    if (!valid) {
      const first = scope.querySelector(".border-red-400")
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    // Collect form template data into hidden field
    this._syncFormData()

    // Serialize APR dynamic row data before submit
    if (isApr) {
      this.aprFieldsTarget.dispatchEvent(new CustomEvent("apr:serialize", { bubbles: true }))
    }

    // Serialize CDV data before submit
    if (isCdv) {
      this.cdvFieldsTarget.dispatchEvent(new CustomEvent("cdv:serialize", { bubbles: true }))
    }

    // Serialize DDS data before submit
    if (isDds) {
      this.ddsFieldsTarget.dispatchEvent(new CustomEvent("dds:serialize", { bubbles: true }))
    }

    // Serialize Architect data before submit
    if (isArchitect) {
      const architectEl = this.architectFieldsTarget.querySelector("[data-controller='architect-form']")
      if (architectEl) architectEl.dispatchEvent(new CustomEvent("architect:serialize", { bubbles: true }))
    }

    // Preserve which button was clicked (publish value)
    const submitter = e.submitter
    if (submitter && submitter.name && !this.element.querySelector(`input[type="hidden"][name="${submitter.name}"]`)) {
      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = submitter.name
      hidden.value = submitter.value
      this.element.appendChild(hidden)
    }

    // Show spinner on the clicked button, lock width
    if (submitter) {
      const w = submitter.offsetWidth
      submitter.style.width = `${w}px`
      submitter.innerHTML = `<svg class="w-5 h-5 animate-spin mx-auto" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
      submitter.disabled = true
    }
    // Disable the other button too
    if (this.hasDraftBtnTarget) this.draftBtnTarget.disabled = true
    if (this.hasPublishBtnTarget) this.publishBtnTarget.disabled = true
    if (this.hasPublishDropdownToggleTarget) this.publishDropdownToggleTarget.disabled = true

    // Add distribute hidden field if user chose "Publish & Distribute"
    if (this._wantsDistribute) {
      let distributeInput = this.element.querySelector('input[type="hidden"][name="distribute"]')
      if (!distributeInput) {
        distributeInput = document.createElement("input")
        distributeInput.type = "hidden"
        distributeInput.name = "distribute"
        this.element.appendChild(distributeInput)
      }
      distributeInput.value = "true"
      this._wantsDistribute = false
    } else {
      const existing = this.element.querySelector('input[type="hidden"][name="distribute"]')
      if (existing) existing.remove()
    }

    // Use submit() instead of requestSubmit() to avoid Turbo's two-phase
    // submit event handling swallowing the re-dispatched event
    this.element.submit()
  }

  publishAndDistribute(event) {
    event.preventDefault()
    event.stopPropagation()

    // Close dropdown
    if (this.hasPublishDropdownMenuTarget) {
      this.publishDropdownMenuTarget.classList.add("hidden")
    }

    // Set flag so validateAndSubmit includes distribute param
    this._wantsDistribute = true

    // Trigger the same validation flow by simulating submit on the publish button
    this.publishBtnTarget.click()
  }

  _showFieldError(field, message) {
    field.classList.add("border-red-400", "ring-1", "ring-red-400")
    const msg = field.parentElement.querySelector("[data-error-msg]") ||
                field.closest("div").querySelector("[data-error-msg]")
    if (msg) {
      msg.textContent = message
      msg.classList.remove("hidden")
    }
    // Auto-clear on interaction
    const event = field.tagName === "SELECT" ? "change" : "input"
    field.addEventListener(event, () => this._clearFieldError(field), { once: true })
  }

  _clearFieldError(field) {
    field.classList.remove("border-red-400", "ring-1", "ring-red-400")
    const msg = field.parentElement.querySelector("[data-error-msg]") ||
                field.closest("div").querySelector("[data-error-msg]")
    if (msg) msg.classList.add("hidden")
  }

  // ── Word counter ─────────────────────────────────────────────────────

  // Grow the notes box with its content (typed or AI-inserted) so nothing
  // hides behind a scrollbar; the user can still drag it taller.
  _autosizeNotes() {
    if (!this.hasNotesFieldTarget) return
    const field = this.notesFieldTarget
    field.style.overflowY = "hidden"
    field.style.height = "auto"
    field.style.height = `${Math.max(field.scrollHeight + 2, 96)}px`
  }

  updateWordCount() {
    this._autosizeNotes()
    if (!this.hasNotesFieldTarget || !this.hasWordCountTarget) return
    const text = this.notesFieldTarget.value.trim()
    const count = text ? text.split(/\s+/).length : 0
    // No data-min-words attribute (builder companies) = no minimum, so the
    // counter never turns red for a short note.
    const min = parseInt(this.notesFieldTarget.dataset.minWords || "0")
    this.wordCountTarget.textContent = `${count} word${count !== 1 ? "s" : ""}`
    this.wordCountTarget.classList.toggle("text-red-500", count > 0 && count < min)
    this.wordCountTarget.classList.toggle("text-gray-400", count === 0 || count >= min)
  }

  // ── Report type change ──────────────────────────────────────────────

  typeChanged() {
    if (!this.hasReportNumberTarget) return
    const typeId = this.reportTypeTarget.value
    const numbers = this.reportNumbersByTypeValue || {}
    if (numbers[typeId] !== undefined) {
      this.reportNumberTarget.value = numbers[typeId]
    } else if (this.nextReportNumberUrlValue) {
      this._fetchNextReportNumber(typeId)
    }
    this._renderFormTemplate()
    this._toggleNotes()
    this._toggleCustomForms()
    this._toggleWeatherSwitch()
  }

  // Custom-form types (AI-built templates) carry their own fields — the
  // generic Notes box is not on their paper form (Phelipe 2026-09-07).
  _toggleNotes() {
    if (!this.hasNotesBlockTarget) return
    const typeId = this.hasReportTypeTarget ? this.reportTypeTarget.value : null
    const schema = typeId ? (this.formTemplatesByTypeValue || {})[typeId] : null
    // keep_builtin (Studio, 2026-10-17): a built-in type's own template only ADDS
    // fields under the built-in ones, so Notes stays.
    const hasTemplate = !!(schema && schema.sections && schema.sections.length) && !(schema && schema.keep_builtin)
    this.notesBlockTarget.classList.toggle("hidden", hasTemplate)
    if (this.hasNotesFieldTarget) this.notesFieldTarget.disabled = hasTemplate
  }

  // Show the "Include automatic weather" switch only for report types that
  // allow it. Inputs are disabled while hidden so the report keeps its
  // default (on) when the type doesn't offer the choice.
  _toggleWeatherSwitch() {
    if (!this.hasWeatherSwitchWrapTarget) return
    const ids = (this.weatherToggleTypeIdsValue || []).map(String)
    const show = ids.includes(String(this.reportTypeTarget.value))
    this.weatherSwitchWrapTarget.classList.toggle("hidden", !show)
    if (this.hasWeatherSwitchInputTarget) this.weatherSwitchInputTarget.disabled = !show
    if (this.hasWeatherSwitchHiddenTarget) this.weatherSwitchHiddenTarget.disabled = !show
  }

  // ── New report type / template import ──────────────────────────────
  // The pop-up, the AI import and the editor live in lib/form_template_editor
  // (shared with the settings pages). This is the report form's side of it:
  // the created type joins the dropdown, gets selected, and its form renders.
  // A type made from a Design project also shows in Design, so it stays in
  // this project's list (new types otherwise start in Construction only).
  _newTypeExtraParams() {
    return this.designPhaseValue ? { show_in_design: true } : {}
  }

  _onTemplateTypeSaved(data) {
    if (data.form_template_schema) {
      this.formTemplatesByTypeValue = Object.assign({}, this.formTemplatesByTypeValue, { [String(data.id)]: data.form_template_schema })
    }
    if (![...this.reportTypeTarget.options].some(o => o.value === String(data.id))) {
      const opt = document.createElement("option")
      opt.value = data.id
      opt.textContent = data.name
      opt.dataset.name = data.name
      this.reportTypeTarget.appendChild(opt)
    }
    this.reportTypeTarget.value = String(data.id)
    this.typeChanged()
    this.closeTemplatePreview()
    this.closeNewTypeModal()
    this._flashToast(data.form_template_schema
      ? `Report type “${data.name}” added — the form is ready below.`
      : `Report type “${data.name}” added.`)
  }

  async _fetchNextReportNumber(typeId) {
    try {
      const res = await fetch(`${this.nextReportNumberUrlValue}?report_type_id=${typeId}`, {
        headers: { "Accept": "application/json" }
      })
      if (!res.ok) return
      const data = await res.json()
      if (data.next_report_number && this.hasReportNumberTarget) {
        this.reportNumberTarget.value = data.next_report_number
      }
    } catch (e) {
      console.error("Failed to fetch next report number:", e)
    }
  }

  // ── Custom form toggle (APR, DDS, etc.) ────────────────────────────

  _toggleCustomForms() {
    const typeName = this.hasReportTypeTarget
      ? (this.reportTypeTarget.selectedOptions[0]?.dataset?.name || "")
      : ""
    const isApr = typeName === "APR"
    const isRdo = typeName === "RDO"
    const isCdv = typeName === "Checklist de Veículos"
    const isDds = typeName === "DDS"
    const isRdm = typeName === "Recebimento de Material"
    const isArchitect = typeName === "Architect Weekly Field Report"

    const isCustom = isApr || isRdo || isCdv || isDds || isRdm || isArchitect
    if (this.hasStandardFieldsTarget) this.standardFieldsTarget.classList.toggle("hidden", isCustom)
    if (this.hasAprFieldsTarget) this.aprFieldsTarget.classList.toggle("hidden", !isApr)
    if (this.hasRdoFieldsTarget) this.rdoFieldsTarget.classList.toggle("hidden", !isRdo)
    if (this.hasCdvFieldsTarget) this.cdvFieldsTarget.classList.toggle("hidden", !isCdv)
    if (this.hasDdsFieldsTarget) this.ddsFieldsTarget.classList.toggle("hidden", !isDds)
    if (this.hasRdmFieldsTarget) this.rdmFieldsTarget.classList.toggle("hidden", !isRdm)
    if (this.hasArchitectFieldsTarget) this.architectFieldsTarget.classList.toggle("hidden", !isArchitect)

    // Disable inputs in hidden sections so duplicate-named fields (e.g. narration)
    // don't overwrite the active section's values during native form submit
    const sections = [
      [this.hasStandardFieldsTarget, this.standardFieldsTarget, isCustom],
      [this.hasAprFieldsTarget, this.aprFieldsTarget, !isApr],
      [this.hasRdoFieldsTarget, this.rdoFieldsTarget, !isRdo],
      [this.hasCdvFieldsTarget, this.cdvFieldsTarget, !isCdv],
      [this.hasDdsFieldsTarget, this.ddsFieldsTarget, !isDds],
      [this.hasRdmFieldsTarget, this.rdmFieldsTarget, !isRdm],
      [this.hasArchitectFieldsTarget, this.architectFieldsTarget, !isArchitect],
    ]
    sections.forEach(([has, target, isHidden]) => {
      if (!has) return
      target.querySelectorAll("input, textarea, select").forEach(el => {
        el.disabled = isHidden
      })
    })

    // Weather Delay + Workers at Jobsite are daily-log metrics: hide + disable
    // them for any report type that isn't a daily log (Testing / Inspection /
    // Safety / Weekly, etc.) so they neither show nor block submit
    // (people_count is otherwise required). Runs after the loop above so it
    // wins over standardFields re-enabling its inputs. (Notes stays — it lives
    // outside dailyOnlyFields.)
    if (this.hasDailyOnlyFieldsTarget) {
      // Only the Daily Log / Daily Report types carry these — "daily" alone
      // matched too much (Phelipe 2026-09-01, item 32). Extended from the
      // builder module to the owner module too (Phelipe 2026-09-03); keep in
      // lockstep with DailyReport#daily_metrics_applicable?.
      // Daily Log / Daily Report / Diário de Obra (Phelipe 2026-09-06) —
      // keep in lockstep with DailyReport::DAILY_TYPE_MARKERS.
      const dn = typeName.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
      const showDailyOnly = !isCustom && ["daily log", "daily report", "diario de obra"].some(m => dn.includes(m))
      this.dailyOnlyFieldsTarget.classList.toggle("hidden", !showDailyOnly)
      this.dailyOnlyFieldsTarget.querySelectorAll("input, textarea, select")
        .forEach(el => { el.disabled = !showDailyOnly })
    }
  }

  // ── Form template fields ───────────────────────────────────────────

  // The fresh schema of a template Studio changed, for every report type that uses it.
  _studioTemplate(live) {
    if (!live.schema || !Array.isArray(live.type_ids)) return
    const map = Object.assign({}, this.formTemplatesByTypeValue || {})
    live.type_ids.forEach((id) => { map[String(id)] = live.schema })
    this.formTemplatesByTypeValue = map
    this._renderFormTemplate()
  }

  _renderFormTemplate() {
    if (!this.hasFormTemplateFieldsTarget) return
    const container = this.formTemplateFieldsTarget
    const typeId = this.hasReportTypeTarget ? this.reportTypeTarget.value : null
    const templates = this.formTemplatesByTypeValue || {}
    const schema = typeId ? templates[typeId] : null

    const anchors = [...this.element.querySelectorAll("[data-studio-anchor]")].filter((a) => a !== container)
    anchors.forEach((a) => { a.innerHTML = "" })
    if (!schema || !schema.sections || schema.sections.length === 0) {
      container.innerHTML = ""
      return
    }

    const data = this.formDataValue || {}
    if (schema.keep_builtin && anchors.length) {
      // A built-in type's own fields (Studio, 2026-10-17): each section sits
      // at its place on the form — above Weather Delay, under Workers, under
      // Notes, under Attachments — in the box marked with that anchor.
      const placeOf = (sec) => sec.anchor || "after_notes"
      const boxes = { after_notes: container }
      anchors.forEach((a) => { boxes[a.dataset.studioAnchor] = a })
      const grouped = {}
      schema.sections.forEach((sec) => {
        const place = boxes[placeOf(sec)] ? placeOf(sec) : "after_notes"
        ;(grouped[place] ||= []).push(sec)
      })
      container.innerHTML = ""
      Object.entries(grouped).forEach(([place, sections]) => {
        boxes[place].innerHTML = this._formTemplateHtml({ sections }, data)
      })
    } else {
      container.innerHTML = this._formTemplateHtml(schema, data)
    }
    if (!this._showIfBound) {
      // On the whole form: the fields can sit in any of the anchor boxes.
      this.element.addEventListener("change", () => this._applyShowIf())
      this._showIfBound = true
    }
    this._applyShowIf()
  }

  // Hide/show follow-ups. Field level: the wrapper carries the controlling
  // field key; grid level: a cell carries the controlling column key and is
  // judged against the same row. Hidden inputs are disabled so a stale
  // answer never posts.
  _applyShowIf() {
    if (!this.hasFormTemplateFieldsTarget) return
    const root = this.element
    const matches = (value, expected) => {
      const v = String(value == null ? "" : value).trim().toLowerCase()
      const e = String(expected == null ? "Yes" : expected).trim().toLowerCase()
      if (e === "yes") return v === "yes" || v === "1" || v === "true"
      if (e === "no") return v === "no" || v === "0" || v === "false"
      return v === e
    }
    const toggle = (el, show) => {
      el.classList.toggle("hidden", !show)
      el.querySelectorAll("input, select, textarea").forEach(i => { i.disabled = !show })
    }
    root.querySelectorAll("[data-show-if-field]").forEach(el => {
      const value = this._templateAnswer(root, `form_data[${el.dataset.showIfField}]`)
      toggle(el, matches(value, el.dataset.showIfEquals))
    })
    root.querySelectorAll("[data-show-if-col]").forEach(el => {
      const row = el.closest("[data-row-key]")
      if (!row) return
      const ctl = row.querySelector(`[data-cell-col="${CSS.escape(el.dataset.showIfCol)}"]`)
      const input = ctl ? ctl.querySelector("input, select, textarea") : null
      const value = input ? this._templateAnswer(row, input.name) : ""
      toggle(el, matches(value, el.dataset.showIfEquals))
    })
  }

  _templateAnswer(scope, name) {
    const inputs = [...scope.querySelectorAll(`[name="${CSS.escape(name)}"], [name="${CSS.escape(name)}[]"]`)]
    if (!inputs.length) return ""
    const radio = inputs.find(i => i.type === "radio" && i.checked)
    if (radio) return radio.value
    if (inputs.some(i => i.type === "radio")) return ""
    const boxes = inputs.filter(i => i.type === "checkbox")
    if (boxes.length) return boxes.filter(b => b.checked).map(b => b.value).join(",")
    return inputs[inputs.length - 1].value
  }

  // Team members + directory contacts as browser suggestions for the
  // "Someone else" email box; any other address can still be typed.
  _fillSigCandidates() {
    if (!this.hasSigCandidatesTarget) return
    this.sigCandidatesTarget.innerHTML = (this.signerCandidatesValue || []).map(c =>
      `<option value="${this._escAttr(c.email)}">${this._escHtml(c.name || c.email)}${c.company ? ` · ${this._escHtml(c.company)}` : ""} (${c.kind === "directory" ? "Directory" : "Team"})</option>`
    ).join("")
  }

  sigOtherEmailInput() {
    if (!this.hasSigOtherEmailTarget || !this.hasSigOtherNameTarget) return
    const email = this.sigOtherEmailTarget.value.trim().toLowerCase()
    const match = (this.signerCandidatesValue || []).find(c => String(c.email).toLowerCase() === email)
    if (match && !this._sigOtherNameTyped) this.sigOtherNameTarget.value = match.name || ""
  }

  _templateFieldByKey(key) {
    const typeId = this.hasReportTypeTarget ? this.reportTypeTarget.value : null
    const schema = typeId ? (this.formTemplatesByTypeValue || {})[typeId] : null
    for (const section of (schema?.sections || [])) {
      const f = (section.fields || []).find(x => x.key === key)
      if (f) return f
    }
    return null
  }

  openSignature(e) {
    if (e) e.preventDefault()
    if (!this.hasSigModalTarget) return
    this._sigKey = e.currentTarget.dataset.sigKey
    const current = this._parseSignature(this.element.querySelector(`[data-sig-input="${CSS.escape(this._sigKey)}"]`)?.value)
    const isRequest = current && current.kind === "request"
    if (this.hasSigNameTarget) this.sigNameTarget.value = (current && !isRequest && current.name) || this.signerNameValue || ""
    if (this.hasSigOtherEmailTarget) this.sigOtherEmailTarget.value = isRequest ? (current.email || "") : ""
    if (this.hasSigOtherNameTarget) this.sigOtherNameTarget.value = isRequest ? (current.name || "") : ""
    this._sigOtherNameTyped = false
    if (this.hasSigOtherNameTarget) this.sigOtherNameTarget.oninput = () => { this._sigOtherNameTyped = true }
    this._fillSigCandidates()
    this._sigKind = e.currentTarget.dataset.sigMode === "request" ? "request" : "typed"
    this._applySigKind()
    this.sigRefreshPreview()
    if (!this._sigPadReady && this.hasSigPadTarget) { this._initSigPad(); this._sigPadReady = true }
    this.sigClearPad()
    if (this.hasSigErrorTarget) this.sigErrorTarget.classList.add("hidden")
    this.sigModalTarget.classList.remove("hidden")
    setTimeout(() => { if (this.hasSigNameTarget) this.sigNameTarget.focus() }, 50)
  }

  closeSignature(e) {
    if (e) e.preventDefault()
    if (this.hasSigModalTarget) this.sigModalTarget.classList.add("hidden")
  }

  stopSignature(e) { if (e) e.stopPropagation() }

  sigPickKind(e) {
    this._sigKind = e.currentTarget.dataset.kind
    this._applySigKind()
  }

  _applySigKind() {
    this.sigKindTabTargets.forEach(tab => {
      const on = tab.dataset.kind === this._sigKind
      tab.classList.toggle("bg-white", on)
      tab.classList.toggle("shadow-sm", on)
      tab.classList.toggle("font-medium", on)
      tab.classList.toggle("text-gray-800", on)
      tab.classList.toggle("text-gray-600", !on)
    })
    if (this.hasSigTypedPanelTarget) this.sigTypedPanelTarget.classList.toggle("hidden", this._sigKind !== "typed")
    if (this.hasSigDrawnPanelTarget) this.sigDrawnPanelTarget.classList.toggle("hidden", this._sigKind !== "drawn")
    if (this.hasSigOtherPanelTarget) this.sigOtherPanelTarget.classList.toggle("hidden", this._sigKind !== "request")
    if (this.hasSigSelfBlockTarget) this.sigSelfBlockTarget.classList.toggle("hidden", this._sigKind === "request")
    if (this.hasSigConfirmBtnTarget) {
      const b = this.sigConfirmBtnTarget
      b.textContent = this._sigKind === "request" ? (b.dataset.labelSend || "Send for signature") : (b.dataset.labelSign || "Adopt and sign")
    }
    if (this._sigKind === "request" && this.hasSigOtherEmailTarget) setTimeout(() => this.sigOtherEmailTarget.focus(), 50)
  }

  sigRefreshPreview() {
    if (this.hasSigTypedPreviewTarget && this.hasSigNameTarget) {
      this.sigTypedPreviewTarget.textContent = this.sigNameTarget.value.trim() || "Your name"
    }
  }

  _initSigPad() {
    const canvas = this.sigPadTarget
    const ctx = canvas.getContext("2d")
    ctx.lineWidth = 2.2
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.strokeStyle = "#1f2937"
    let drawing = false
    const pos = (ev) => {
      const r = canvas.getBoundingClientRect()
      return [(ev.clientX - r.left) * (canvas.width / r.width), (ev.clientY - r.top) * (canvas.height / r.height)]
    }
    canvas.addEventListener("pointerdown", (ev) => {
      ev.preventDefault()
      drawing = true
      canvas.setPointerCapture(ev.pointerId)
      ctx.beginPath()
      ctx.moveTo(...pos(ev))
    })
    canvas.addEventListener("pointermove", (ev) => {
      if (!drawing) return
      ev.preventDefault()
      ctx.lineTo(...pos(ev))
      ctx.stroke()
      this._sigPadDirty = true
    })
    const stop = () => { drawing = false }
    canvas.addEventListener("pointerup", stop)
    canvas.addEventListener("pointercancel", stop)
  }

  sigClearPad() {
    if (!this.hasSigPadTarget) return
    const ctx = this.sigPadTarget.getContext("2d")
    ctx.clearRect(0, 0, this.sigPadTarget.width, this.sigPadTarget.height)
    this._sigPadDirty = false
  }

  sigConfirm(e) {
    if (e) e.preventDefault()
    const name = this.hasSigNameTarget ? this.sigNameTarget.value.trim() : ""
    const fail = (msg) => {
      if (this.hasSigErrorTarget) { this.sigErrorTarget.textContent = msg; this.sigErrorTarget.classList.remove("hidden") }
    }
    if (this._sigKind === "request") {
      const email = this.hasSigOtherEmailTarget ? this.sigOtherEmailTarget.value.trim().toLowerCase() : ""
      const other = this.hasSigOtherNameTarget ? this.sigOtherNameTarget.value.trim() : ""
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return fail("Enter a valid email address.")
      this._applySignature(this._sigKey, { kind: "request", name: other, email })
      this.closeSignature()
      return
    }
    if (!name) return fail("Type your full name first.")
    if (this._sigKind === "drawn" && !this._sigPadDirty) return fail("Draw your signature first, or switch to Type.")
    const sig = { kind: this._sigKind, name, signed_at: new Date().toISOString() }
    if (this._sigKind === "drawn") sig.data = this.sigPadTarget.toDataURL("image/png")
    this._applySignature(this._sigKey, sig)
    this.closeSignature()
  }

  _applySignature(key, sig) {
    const block = this.element.querySelector(`[data-sig-block="${CSS.escape(key)}"]`)
    const field = this._templateFieldByKey(key) || { key, label: "" }
    if (block) block.outerHTML = this._renderSignatureField(field, sig, `form_data[${key}]`)
  }

  _syncFormData() {
    // No-op: form template fields use native name attributes (form_data[key])
    // and are submitted directly with the form. Nothing to sync.
  }

  // ── Existing data (edit mode) ───────────────────────────────────────

  // ── Photos on grid rows (Larry 2026-09-08 call) ──────────────────────
  // Each grid row carries a paperclip: take a photo / pick one, it uploads
  // through the report's normal photo flow (so it also lands in the
  // report's photo list) and its id is kept on the row as __photos, which
  // the report page, the PDFs and the signing page draw as thumbnails.
  _initRowPhotos() {
    ;(this.existingPhotosValue || []).forEach(p => { if (p && p.id && p.thumbnail_url) this._thumbById[p.id] = p.thumbnail_url })
    const data = this.formDataValue || {}
    Object.entries(data).forEach(([tk, rows]) => {
      if (!rows || typeof rows !== "object" || Array.isArray(rows)) return
      Object.entries(rows).forEach(([rk, cells]) => {
        if (cells && typeof cells === "object" && String(cells.__flag) === "1") {
          if (!this._rowFlags[tk]) this._rowFlags[tk] = {}
          this._rowFlags[tk][rk] = true
        }
        const ids = cells && typeof cells === "object" && Array.isArray(cells.__photos) ? cells.__photos : null
        if (!ids || !ids.length) return
        if (!this._rowPhotos[tk]) this._rowPhotos[tk] = {}
        this._rowPhotos[tk][rk] = ids.map(id => parseInt(id, 10)).filter(Boolean)
      })
    })
  }

  _rowPhotoStrip(tableKey, rowKey, savedIds = []) {
    const ids = (this._rowPhotos[tableKey] && this._rowPhotos[tableKey][rowKey]) || savedIds.map(id => parseInt(id, 10)).filter(Boolean)
    const tk = this._escAttr(tableKey), rk = this._escAttr(rowKey)
    const clip = `<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"/></svg>`
    const thumbs = ids.map(id => {
      const src = this._thumbById[id]
      const img = src ? `<img src="${this._escAttr(src)}" alt="" class="w-7 h-7 rounded object-cover border border-gray-200">`
        : `<span class="w-7 h-7 rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-400">${clip}</span>`
      return `<span class="relative inline-block">${img}<button type="button" data-action="click->report-form#removeRowPhoto" data-table-key="${tk}" data-row-key="${rk}" data-photo-id="${id}" title="Remove from this item" class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white border border-gray-300 text-gray-500 text-[11px] leading-none cursor-pointer">×</button></span>`
    }).join("")
    // "Needs attention" flag (Larry 2026-09-08): what the reviewer must look
    // at — listed in their email and ticked off on the signing page.
    // Icon-only buttons (Phelipe 2026-09-10) so the column stays narrow and
    // the grid keeps fitting the form without a sideways scroll.
    const flagged = !!(this._rowFlags[tableKey] && this._rowFlags[tableKey][rowKey])
    const flag = `<svg class="w-4 h-4" fill="${flagged ? "currentColor" : "none"}" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5"/></svg>`
    const btn = "inline-flex items-center justify-center w-7 h-7 rounded border cursor-pointer"
    const flagBtn = `<button type="button" data-action="click->report-form#toggleRowFlag" data-table-key="${tk}" data-row-key="${rk}" aria-pressed="${flagged}" title="${flagged ? "Flagged for the reviewer — click to clear" : "Flag this item for the reviewer"}"
              class="${btn} ${flagged ? "bg-amber-100 text-amber-700 border-amber-300" : "border-dashed border-gray-300 text-gray-400 hover:text-amber-700 hover:border-amber-400"}">${flag}</button>`
    return `<div class="flex items-center gap-1 font-normal" data-row-photos="${tk}|${rk}">${thumbs}
      <button type="button" data-action="click->report-form#pickRowPhoto" data-table-key="${tk}" data-row-key="${rk}" title="Add a photo to this item"
              class="${btn} border-dashed border-gray-300 text-gray-400 hover:text-brand hover:border-brand">${clip}</button>
      ${flagBtn}
    </div>`
  }

  pickRowPhoto(e) {
    if (!this.hasRowPhotoInputTarget) return
    this._rowPhotoTarget = { tk: e.currentTarget.dataset.tableKey, rk: e.currentTarget.dataset.rowKey }
    this.rowPhotoInputTarget.click()
  }

  rowPhotoChosen(e) {
    const files = [...(e.target.files || [])].filter(f => this._isImageFile(f))
    e.target.value = ""
    const tag = this._rowPhotoTarget
    if (!files.length || !tag) return
    this._addPhotoFiles(files, tag)
  }

  _attachRowPhoto(tag, photoId, thumbUrl) {
    if (!tag || !photoId) return
    if (!this._rowPhotos[tag.tk]) this._rowPhotos[tag.tk] = {}
    const list = this._rowPhotos[tag.tk][tag.rk] || (this._rowPhotos[tag.tk][tag.rk] = [])
    if (!list.includes(photoId)) list.push(photoId)
    if (thumbUrl) this._thumbById[photoId] = thumbUrl
    this._refreshRowPhotoStrip(tag.tk, tag.rk)
    this._syncHiddenFields()
  }

  toggleRowFlag(e) {
    const { tableKey: tk, rowKey: rk } = e.currentTarget.dataset
    if (!this._rowFlags[tk]) this._rowFlags[tk] = {}
    this._rowFlags[tk][rk] = !this._rowFlags[tk][rk]
    this._refreshRowPhotoStrip(tk, rk)
    this._syncHiddenFields()
  }

  removeRowPhoto(e) {
    const { tableKey: tk, rowKey: rk } = e.currentTarget.dataset
    const id = parseInt(e.currentTarget.dataset.photoId, 10)
    const list = this._rowPhotos[tk] && this._rowPhotos[tk][rk]
    if (list) this._rowPhotos[tk][rk] = list.filter(x => x !== id)
    this._refreshRowPhotoStrip(tk, rk)
    this._syncHiddenFields()
  }

  _refreshRowPhotoStrip(tk, rk) {
    const el = this.element.querySelector(`[data-row-photos="${CSS.escape(tk)}|${CSS.escape(rk)}"]`)
    if (!el) return
    el.outerHTML = this._rowPhotoStrip(tk, rk)
  }

  _initExisting() {
    const existingPhotos = this.existingPhotosValue || []
    existingPhotos.forEach(p => {
      this._photoIds.push(p.id)
      if (p.comment) this._photoComments[p.id] = p.comment
      this._renderExistingPhotoRow(p)
    })

    const existingFiles = this.existingFilesValue || []
    existingFiles.forEach(f => {
      this._fileIds.push(f.id)
      this._renderExistingFileChip(f)
    })

    this._syncHiddenFields()
  }

  _renderExistingPhotoRow(photo) {
    if (!this.hasPhotoListTarget) return

    const row = document.createElement("div")
    row.className = "flex items-start gap-2 p-2 rounded-lg"
    row.classList.add("photo-row-bg")
    row.dataset.photoId = photo.id

    // Thumbnail
    const thumbDiv = document.createElement("div")
    thumbDiv.className = "w-14 h-14 rounded shrink-0 overflow-hidden flex items-center justify-center"
    thumbDiv.classList.add("photo-thumb-bg")
    if (photo.thumbnail_url) {
      const img = document.createElement("img")
      img.className = "w-full h-full object-cover"
      img.src = photo.thumbnail_url
      img.onerror = () => {
        img.remove()
        thumbDiv.innerHTML = this._imagePlaceholderSvg()
      }
      thumbDiv.appendChild(img)
    } else {
      thumbDiv.innerHTML = this._imagePlaceholderSvg()
    }
    row.appendChild(thumbDiv)

    // Info + comment
    const info = document.createElement("div")
    info.className = "flex-1 min-w-0"
    const nameP = document.createElement("p")
    nameP.className = "font-medium text-gray-700 truncate"
    nameP.style.fontSize = "13px"
    nameP.textContent = photo.filename || "Photo"
    info.appendChild(nameP)

    const commentInput = document.createElement("input")
    commentInput.type = "text"
    commentInput.placeholder = this.i18nAddCommentValue || "Add comment..."
    commentInput.value = photo.comment || ""
    commentInput.className = "mt-1 w-full px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-brand focus:border-brand"
    commentInput.style.fontSize = "13px"
    commentInput.addEventListener("input", () => {
      this._photoComments[photo.id] = commentInput.value
      this._syncHiddenFields()
    })
    info.appendChild(commentInput)

    // Activity link (photo-schedule integration)
    this._renderActivityLink(photo.id, info, photo.task_id || null, photo.percent_complete || null)

    row.appendChild(info)

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "p-1 text-gray-400 hover:text-gray-600 shrink-0"
    removeBtn.innerHTML = this._xSvg()
    removeBtn.addEventListener("click", () => {
      row.remove()
      this._photoIds = this._photoIds.filter(id => id !== photo.id)
      delete this._photoComments[photo.id]
      delete this._photoTasks[photo.id]
      this._removedIds.push(photo.id)
      this._syncHiddenFields()
    })
    row.appendChild(removeBtn)

    this.photoListTarget.appendChild(row)
  }

  _renderExistingFileChip(file) {
    if (!this.hasFileListTarget) return
    const chip = this._createFileChip(file.filename, {
      removable: true,
      video: !!file.is_video,
      onRemove: () => {
        chip.remove()
        this._fileIds = this._fileIds.filter(id => id !== file.id)
        this._removedIds.push(file.id)
        this._syncHiddenFields()
      }
    })
    this.fileListTarget.appendChild(chip)
  }

  // ── Single drop zone ────────────────────────────────────────────────

  triggerFileInput() {
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  handleFiles(e) {
    if (e.target.files.length) this._sortAndUpload(Array.from(e.target.files))
    e.target.value = ""
  }

  dragOver(e) {
    e.preventDefault()
    if (this.hasDropzoneTarget) {
      // The AI-reads-files zone is purple (Larry 2026-08-25) — its drag
      // highlight stays in the same family instead of flipping to brand.
      const purple = this.dropzoneTarget.dataset.aiZone === "1"
      this.dropzoneTarget.classList.add(...(purple ? ["border-purple-500", "bg-purple-100"] : ["border-brand", "bg-brand/5"]))
    }
  }

  dragLeave(e) {
    e.preventDefault()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-brand", "bg-brand/5", "border-purple-500", "bg-purple-100")
    }
  }

  dropFiles(e) {
    e.preventDefault()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("border-brand", "bg-brand/5")
    }
    if (e.dataTransfer.files.length) {
      this._sortAndUpload(Array.from(e.dataTransfer.files))
    }
  }

  // Sort dropped files into images vs non-images and route to correct upload flow
  _sortAndUpload(files) {
    const imageFiles = []
    const videoFiles = []
    const otherFiles = []

    files.forEach(file => {
      if (this._isImageFile(file)) {
        imageFiles.push(file)
      } else if (this._isVideoFile(file)) {
        videoFiles.push(file)
      } else {
        otherFiles.push(file)
      }
    })

    if (imageFiles.length) this._addPhotoFiles(imageFiles)
    if (videoFiles.length) this._addVideoFiles(videoFiles)
    if (otherFiles.length) this._addFiles(otherFiles)
  }

  // The kinds the server treats as videos (Attachment#is_video?).
  _isVideoFile(file) {
    const ext = (file.name || "").split(".").pop().toLowerCase()
    return VIDEO_EXTENSIONS.includes(ext)
  }

  _isImageFile(file) {
    if (file.type.startsWith("image/")) return true
    if (this._isHeic(file)) return true
    const ext = file.name.split(".").pop().toLowerCase()
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff", "dng"].includes(ext)
  }

  // ── Photo upload ────────────────────────────────────────────────────

  _addPhotoFiles(fileList, rowTag = null) {
    const newEntries = []
    fileList.forEach(file => {
      const entry = { file, comment: "", convertedBlob: null, _uploadResult: null, rowTag }
      this._uploadPhotos.push(entry)
      const index = this._uploadPhotos.length - 1
      this._renderPhotoUploadRow(index, file)
      newEntries.push({ entry, index })

      if (this._isHeic(file)) {
        entry._heicPromise = this._convertHeic(file, index, entry)
      }

      // Read EXIF (taken-at + GPS) from the ORIGINAL file — compression
      // re-encodes through canvas and strips it
      entry._exifPromise = readCaptureMetadata(file)
        .then(meta => { entry.captureMeta = meta })
        .catch(() => {})
    })

    // Track the FULL upload+register flow (not just the S3 PUT) so that
    // validateAndSubmit waits until photo IDs are registered before submitting.
    this._eagerUploadPromises.push(this._eagerPhotoUpload(newEntries))
  }

  _renderPhotoUploadRow(index, file) {
    if (!this.hasPhotoListTarget) return

    const row = document.createElement("div")
    row.className = "flex items-start gap-2 p-2 rounded-lg"
    row.classList.add("photo-row-bg")
    row.dataset.uploadPhotoIndex = index

    // Thumbnail
    const thumbDiv = document.createElement("div")
    thumbDiv.className = "w-14 h-14 rounded shrink-0 overflow-hidden flex items-center justify-center"
    thumbDiv.classList.add("photo-thumb-bg")
    thumbDiv.dataset.role = "upload-thumb"
    if (this._isHeic(file)) {
      thumbDiv.innerHTML = this._spinnerSvg()
    } else if (file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.className = "w-full h-full object-cover"
      const blobUrl = URL.createObjectURL(file)
      img.src = blobUrl
      if (this._uploadPhotos[index]) this._uploadPhotos[index].blobUrl = blobUrl
      img.onerror = () => {
        URL.revokeObjectURL(blobUrl)
        img.remove()
        thumbDiv.innerHTML = this._imagePlaceholderSvg()
      }
      thumbDiv.appendChild(img)
    } else {
      thumbDiv.innerHTML = this._imagePlaceholderSvg()
    }
    row.appendChild(thumbDiv)

    // Info + comment
    const info = document.createElement("div")
    info.className = "flex-1 min-w-0"
    const nameP = document.createElement("p")
    nameP.className = "font-medium text-gray-700 truncate"
    nameP.style.fontSize = "13px"
    nameP.textContent = file.name
    info.appendChild(nameP)

    const commentInput = document.createElement("input")
    commentInput.type = "text"
    commentInput.placeholder = this.i18nAddCommentValue || "Add comment..."
    commentInput.className = "mt-1 w-full px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-brand focus:border-brand"
    commentInput.style.fontSize = "13px"
    commentInput.dataset.uploadPhotoIndex = index
    commentInput.addEventListener("input", () => {
      if (this._uploadPhotos[index]) {
        this._uploadPhotos[index].comment = commentInput.value
        const photoId = this._uploadPhotos[index]?._uploadResult?.photo_id
        if (photoId) {
          this._photoComments[photoId] = commentInput.value
          this._syncHiddenFields()
        }
      }
    })
    info.appendChild(commentInput)

    // Activity link placeholder (rendered once photo is registered and has an ID)
    if (this._photoScheduleEnabled) {
      const activityPlaceholder = document.createElement("div")
      activityPlaceholder.dataset.activityPlaceholder = index
      info.appendChild(activityPlaceholder)
    }

    row.appendChild(info)

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "p-1 text-gray-400 hover:text-gray-600 shrink-0"
    removeBtn.innerHTML = this._xSvg()
    removeBtn.addEventListener("click", () => {
      const entry = this._uploadPhotos[index]
      if (entry?._uploadResult?.photo_id) {
        this._photoIds = this._photoIds.filter(id => id !== entry._uploadResult.photo_id)
        delete this._photoComments[entry._uploadResult.photo_id]
        delete this._photoTasks[entry._uploadResult.photo_id]
        this._syncHiddenFields()
      }
      // Drop the photo from the AI lists too, then refresh the combined
      // photo summary so it no longer describes a removed photo.
      const cache = entry?._uploadResult?.cache_name
      if (cache) {
        this._untrackAiFile(cache)
        if (this._photoAiFiles?.length) {
          this._photoAiFiles = this._photoAiFiles.filter(f => f.cache_name !== cache)
          if (this._photoAiFiles.length) this._schedulePhotoSummary()
          else this._photoSummaryText = null
        }
      }
      this._uploadPhotos[index] = null
      row.remove()
    })
    row.appendChild(removeBtn)

    this.photoListTarget.appendChild(row)
  }

  async _eagerPhotoUpload(entries) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    this._startUpload()

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: entries.map(({ entry }) => ({
            filename: entry.file.name,
            content_type: entry.file.type || "application/octet-stream",
            byte_size: entry.file.size
          }))
        })
      })

      if (!presignRes.ok) {
        console.error("Presign failed")
        return
      }

      const { uploads } = await presignRes.json()

      const uploadPromises = entries.map(async ({ entry, index }, i) => {
        const upload = uploads[i]
        const prepared = await this._prepareFile(entry)

        const res = await fetch(upload.upload_url, {
          method: "PUT",
          headers: { "Content-Type": prepared.blob.type || upload.content_type },
          body: prepared.blob
        })
        if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)

        entry._uploadResult = {
          cache_name: upload.cache_name,
          filename: upload.filename,
          _blobUrl: URL.createObjectURL(prepared.blob)
        }
      })

      await Promise.allSettled(uploadPromises)

      // Register successful uploads
      const successful = entries.filter(({ entry }) => entry?._uploadResult)
      if (successful.length === 0) return

      // Wait for EXIF reads (in practice instant — file headers only)
      await Promise.all(entries.map(({ entry }) => entry._exifPromise).filter(Boolean))

      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(({ entry }) => ({
            cache_name: entry._uploadResult.cache_name,
            filename: entry._uploadResult.filename,
            taken_at: entry.captureMeta?.taken_at ?? null,
            latitude: entry.captureMeta?.latitude ?? null,
            longitude: entry.captureMeta?.longitude ?? null,
            altitude: entry.captureMeta?.altitude ?? null,
            heading: entry.captureMeta?.heading ?? null,
            camera_make: entry.captureMeta?.camera_make ?? null,
            camera_model: entry.captureMeta?.camera_model ?? null,
            exif: entry.captureMeta?.exif ?? null
          }))
        })
      })

      const registered = await registerRes.json()

      registered.forEach((a, i) => {
        const { entry, index } = successful[i]
        entry._uploadResult.photo_id = a.photo_id
        this._photoIds.push(a.photo_id)
        if (entry.comment) this._photoComments[a.photo_id] = entry.comment
        if (entry.rowTag) this._attachRowPhoto(entry.rowTag, a.photo_id, entry.blobUrl || null)

        // Builder projects: photos feed ONLY the photo pipeline — one
        // combined "(All Photos Attached)" line in the notes' AI block plus
        // a per-photo comment — never the per-file document summaries.
        // Owners keep Larry's original flow (photos of paper forms go
        // through the manual "Summarize files with AI" button).
        if (this.autoSummarizeValue && this.hasPhotoAiUrlValue && this.photoAiUrlValue) {
          this._photoAiFiles.push({
            cache_name: entry._uploadResult.cache_name,
            filename: entry._uploadResult.filename,
            attachment_id: a.id
          })
          this._schedulePhotoSummary()
          this._aiPhotoComment(index, entry, a)
        } else {
          this._trackAiFile(entry._uploadResult.cache_name, entry._uploadResult.filename, "image/jpeg", a.id)
        }

        // Render activity link now that we have a photo ID
        if (this._photoScheduleEnabled) {
          const placeholder = this.photoListTarget?.querySelector(`[data-activity-placeholder="${index}"]`)
          if (placeholder) {
            this._renderActivityLink(a.photo_id, placeholder)
          }
        }
      })

      this._syncHiddenFields()

      // Poll for thumbnails
      const photoIds = registered.map(a => a.photo_id)
      if (this.hasPollThumbnailsUrlValue && this.pollThumbnailsUrlValue) {
        this._pollForThumbnails(photoIds)
      }
    } catch (e) {
      console.error("Photo upload failed:", e)
    } finally {
      this._finishUpload()
    }
  }

  // ── File upload (non-image) ─────────────────────────────────────────

  _addFiles(fileList) {
    const entries = []
    // A type the server won't keep is turned away here with a toast — before
    // Issue 41870 an .xlsm looked uploaded and was then lost by the
    // background store step.
    fileList = fileList.filter(file => {
      if (uploadAllowed(file.name)) return true
      const template = this.i18nFileTypeNotAllowedValue || "__NAME__ wasn't added: .__EXT__ files can't be uploaded."
      showToast(template.replace("__NAME__", file.name).replace("__EXT__", uploadExtension(file.name) || "?"), "error")
      return false
    })
    if (fileList.length === 0) return
    fileList.forEach(file => {
      const entry = { file, _uploadResult: null }
      this._uploadFiles.push(entry)
      const index = this._uploadFiles.length - 1
      entries.push({ entry, index })
    })
    // Track the full upload+register flow so validateAndSubmit waits for file
    // IDs to be registered before submitting (otherwise file_ids[] is empty).
    this._eagerUploadPromises.push(this._doFileUpload(entries))
  }

  // ── Videos (K9-6, Larry 2026-10) ───────────────────────────────────
  // "I couldn't get it to accept a video, so I just had to drop the videos
  // into the Photos area." A report takes videos like Photos does: up to
  // 5 GB (past that the too-big pop-up), a chip each while it goes up. Up to
  // 500MB rides the report's own presign → PUT → register; bigger ones go in
  // ~100MB parts through Photos' big-video upload. Saved, a video joins the
  // report and the project's Photos, and the server takes its still and
  // makes the copy that plays everywhere. Videos never go to the AI file
  // summary.

  _addVideoFiles(files) {
    if (!this.hasFileListTarget) return
    const tooBig = files.find(f => f.size > UPLOAD_LIMIT_BYTES)
    if (tooBig) {
      showSizeLimitModal(tooBig, this.sizeLimitTextValue, {
        source: "report", project_id: this.projectIdValue || null, project_phase: this.projectPhaseValue || null
      })
    }

    files.filter(f => f.size <= UPLOAD_LIMIT_BYTES).forEach(file => {
      const entry = { file, isVideo: true, duration: null, _uploadResult: null }
      this._uploadFiles.push(entry)
      entry._durationPromise = this._readVideoDuration(file, entry)
      const upload = file.size > SINGLE_PUT_VIDEO_MAX && this.presignMultipartUrlValue
        ? this._uploadBigVideo(entry)
        : this._uploadVideo(entry)
      this._eagerUploadPromises.push(upload)
    })
  }

  async _uploadVideo(entry) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = { "X-CSRF-Token": token, "Content-Type": "application/json" }
    const file = entry.file
    this._startUpload()
    const placeholder = this._createFileChip(file.name, { uploading: true, video: true })
    this.fileListTarget.appendChild(placeholder)

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST", headers,
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      if (!presignRes.ok) throw new Error((await presignRes.json().catch(() => ({}))).error || "Couldn't start the upload")
      const { uploads } = await presignRes.json()
      const upload = uploads[0]

      const res = await fetch(upload.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || upload.content_type },
        body: file
      })
      if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)

      await entry._durationPromise
      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST", headers,
        body: JSON.stringify({ files: [{ cache_name: upload.cache_name, filename: upload.filename, duration: entry.duration }] })
      })
      if (!registerRes.ok) throw new Error("Couldn't save the upload")
      const [registered] = await registerRes.json()
      entry._uploadResult = { photo_id: registered.photo_id }
      this._placeVideoChip(placeholder, registered.filename || file.name, registered.photo_id)
    } catch (e) {
      console.error("Video upload failed:", e)
      this._videoUploadFailed(placeholder, file)
    } finally {
      this._finishUpload()
    }
  }

  // Past 500MB: Photos' big-video upload (the server makes the photo,
  // hidden, the browser PUTs the parts, complete files it). A failed upload
  // is aborted so nothing half-done stays.
  async _uploadBigVideo(entry) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = { "X-CSRF-Token": token, "Content-Type": "application/json" }
    const file = entry.file
    this._startUpload()
    const placeholder = this._createFileChip(file.name, { uploading: true, video: true })
    const progress = document.createElement("span")
    progress.dataset.chipProgress = ""
    progress.className = "tabular-nums"
    progress.textContent = "0%"
    placeholder.appendChild(progress)
    this.fileListTarget.appendChild(placeholder)

    let session = null
    try {
      const res = await fetch(this.presignMultipartUrlValue, {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size })
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Couldn't start the upload")
      session = await res.json()

      let sent = 0
      for (const part of session.part_urls) {
        if (this._disconnected) throw new Error("cancelled")
        const start = (part.part_number - 1) * session.part_size
        const blob = file.slice(start, Math.min(start + session.part_size, file.size))
        await this._putVideoPart(part.url, blob, loaded => {
          progress.textContent = `${Math.min(99, Math.round(((sent + loaded) / file.size) * 100))}%`
        })
        sent += blob.size
      }

      await entry._durationPromise
      const done = await fetch(this.completeMultipartUrlValue, {
        method: "POST", headers, credentials: "same-origin",
        body: JSON.stringify({ attachment_id: session.attachment_id, upload_id: session.upload_id,
                               expected_parts: session.part_urls.length, duration: entry.duration, source: "report" })
      })
      if (!done.ok) throw new Error((await done.json().catch(() => ({}))).error || "Couldn't finish the upload")
      const finished = await done.json()
      entry._uploadResult = { photo_id: finished.photo_id }
      this._placeVideoChip(placeholder, finished.filename || file.name, finished.photo_id)
    } catch (e) {
      if (session) {
        fetch(this.abortMultipartUrlValue, {
          method: "POST", headers, credentials: "same-origin",
          body: JSON.stringify({ attachment_id: session.attachment_id, upload_id: session.upload_id })
        }).catch(() => {})
      }
      console.error("Video upload failed:", e)
      this._videoUploadFailed(placeholder, file)
    } finally {
      this._finishUpload()
    }
  }

  _putVideoPart(url, blob, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded) }
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)))
      xhr.onerror = () => reject(new Error("Connection lost during the upload"))
      xhr.open("PUT", url)
      xhr.send(blob)
    })
  }

  // The uploaded video's gray chip with its × (like any file), its id sent
  // with the report on save.
  _placeVideoChip(placeholder, name, photoId) {
    this._fileIds.push(photoId)
    const chip = this._createFileChip(name, {
      removable: true,
      video: true,
      onRemove: () => {
        chip.remove()
        this._fileIds = this._fileIds.filter(id => id !== photoId)
        this._syncHiddenFields()
      }
    })
    placeholder.replaceWith(chip)
    this._syncHiddenFields()
  }

  // The chip goes and a toast says which video didn't make it (a silent
  // vanish is what Larry ran into).
  _videoUploadFailed(placeholder, file) {
    placeholder.remove()
    if (this._disconnected) return
    const template = this.i18nVideoUploadFailedValue || "__NAME__ didn't upload. Please try again."
    showToast(template.replace("__NAME__", file.name), "error")
  }

  // The length, read by the browser before upload (null when it can't play
  // it; the server then takes it from the file).
  _readVideoDuration(file, entry) {
    return new Promise(resolve => {
      const video = document.createElement("video")
      video.preload = "metadata"
      const blobUrl = URL.createObjectURL(file)
      const finish = secs => {
        URL.revokeObjectURL(blobUrl)
        video.removeAttribute("src")
        entry.duration = secs
        resolve(secs)
      }
      const timer = setTimeout(() => finish(null), 5000)
      video.onloadedmetadata = () => {
        clearTimeout(timer)
        finish(Number.isFinite(video.duration) ? video.duration : null)
      }
      video.onerror = () => { clearTimeout(timer); finish(null) }
      video.src = blobUrl
    })
  }

  // ── AI summary of uploaded report files (Larry 2026-07-07) ──────────

  // Removing a chip/photo must also drop it from the AI list — otherwise a
  // remove-and-reupload leaves ghost copies and one click summarizes the
  // same document several times.
  _untrackAiFile(cacheName) {
    if (!cacheName || !this._aiFiles?.length) return
    this._aiFiles = this._aiFiles.filter(f => f.cache_name !== cacheName)
    if (!this._aiFiles.length && !this.autoSummarizeValue && this.hasSummarizeWrapTarget) {
      this.summarizeWrapTarget.classList.add("hidden")
    }
  }

  _trackAiFile(cacheName, filename, contentType, attachmentId) {
    if (!cacheName) return
    this._aiFiles ||= []
    // Same file re-tracked (e.g. removed then re-added) → replace, don't stack.
    this._aiFiles = this._aiFiles.filter(f => f.cache_name !== cacheName)
    // attachment_id lets the server read the stored copy once StoreAssetJob
    // has moved the file out of uploads/tmp (it does so within seconds).
    this._aiFiles.push({ cache_name: cacheName, filename: filename, content_type: contentType, attachment_id: attachmentId })
    // Builder (GC) projects summarize automatically as files finish uploading,
    // so the manual button stays hidden there (it reappears only when an auto
    // run must not touch user-edited notes). Owners get the button. The auto
    // run is debounced so a batch of parallel uploads coalesces into one.
    if (this.autoSummarizeValue) {
      this._scheduleAutoSummarize()
    } else if (this.hasSummarizeWrapTarget) {
      this.summarizeWrapTarget.classList.remove("hidden")
    }
  }

  _revealSummarizeButton() {
    if (this.hasSummarizeWrapTarget) this.summarizeWrapTarget.classList.remove("hidden")
  }

  // Writes progress to both status spots: next to the button (below the drop
  // zone) and in the Notes label.
  _setSummaryStatus(html) {
    if (this.hasSummarizeStatusTarget) this.summarizeStatusTarget.innerHTML = html
    if (this.hasAiSummaryStatusTarget) this.aiSummaryStatusTarget.innerHTML = html
  }

  // Put the AI block into the notes: replace our previous insertion verbatim
  // when it's still intact; otherwise append below whatever the user wrote.
  // Returns false when an auto run must not touch the field (the user edited
  // or already had an AI block) — the caller shows a "refresh by hand" hint.
  _insertAiSummary(block, { auto }) {
    if (!this.hasNotesFieldTarget) return false
    const field = this.notesFieldTarget

    if (this._aiInsertedText && field.value.includes(this._aiInsertedText)) {
      field.value = field.value.replace(this._aiInsertedText, block)
    } else if (auto && (this._aiInsertedText || this._notesHadAiBlock)) {
      return false
    } else {
      const own = field.value.trim()
      field.value = own ? own + "\n\n" + block : block
    }
    this._aiInsertedText = block
    this._notesHadAiBlock = false
    this.updateWordCount()
    return true
  }

  _scheduleAutoSummarize() {
    clearTimeout(this._autoSumTimer)
    this._autoSumTimer = setTimeout(() => this._runSummarize({ auto: true }), 1500)
  }

  // Manual "Summarize files with AI" button.
  summarizeFiles() {
    return this._runSummarize({ auto: false })
  }

  async _runSummarize({ auto }) {
    if (!this._aiFiles?.length || !this.hasSummarizeUrlValue) return

    // Auto never touches an AI block the user edited (or one loaded from a
    // previous save) — skip the request and nudge them to refresh by hand.
    if (auto && this.hasNotesFieldTarget) {
      const intact = this._aiInsertedText && this.notesFieldTarget.value.includes(this._aiInsertedText)
      if ((this._aiInsertedText && !intact) || (!this._aiInsertedText && this._notesHadAiBlock)) {
        this._revealSummarizeButton()
        this._setSummaryStatus(`<span class="text-gray-500">New files attached — click “Summarize files with AI” to update your notes.</span>`)
        return
      }
    }

    // One request at a time. If more files land mid-run, queue a re-run so the
    // final summary covers everything (a manual queued run wins over an auto one).
    if (this._summarizing) {
      if (!(this._summarizePending === "manual" && auto)) {
        this._summarizePending = auto ? "auto" : "manual"
      }
      return
    }
    this._summarizing = true

    const start = performance.now()
    clearInterval(this._sumTimer)
    const tick = () => {
      const secs = Math.round((performance.now() - start) / 1000)
      this._setSummaryStatus(`<span class="font-bold ai-rainbow-text">ConsLog AI — Reading the files...</span> <span class="text-gray-500 tabular-nums">${secs}s</span>`)
    }
    tick()
    this._sumTimer = setInterval(tick, 1000)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.summarizeUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ files: this._aiFiles })
      })
      const data = await res.json().catch(() => ({}))
      clearInterval(this._sumTimer)
      if (!res.ok || data.error) {
        this._setSummaryStatus(`<span class="text-red-600">${data.error || "Summary failed — try again."}</span>`)
        return
      }
      this._fileSummaryText = data.summary
      const inserted = this._composeAiBlock({ auto })
      if (inserted) {
        // Don't yank the page while the user is still attaching files.
        if (!auto && this.hasNotesFieldTarget) this.notesFieldTarget.scrollIntoView({ behavior: "smooth", block: "center" })
        this._setSummaryStatus(`<span class="text-green-600">AI summary added to the notes — edit as needed.</span>`)
      } else {
        this._revealSummarizeButton()
        this._setSummaryStatus(`<span class="text-gray-500">New files read — click “Summarize files with AI” to update your notes.</span>`)
      }
      clearTimeout(this._sumDoneTimer)
      this._sumDoneTimer = setTimeout(() => { if (!this._summarizing) this._setSummaryStatus("") }, 6000)
    } catch (e) {
      clearInterval(this._sumTimer)
      this._setSummaryStatus(`<span class="text-red-600">Summary failed — check your connection.</span>`)
    } finally {
      this._summarizing = false
      if (this._summarizePending) {
        const pending = this._summarizePending
        this._summarizePending = null
        this._runSummarize({ auto: pending === "auto" })
      }
    }
  }

  // ── Combined AI summary of all attached photos (into the notes) ─────

  // The notes' AI block holds both sources; EVERY paragraph starts with
  // "AI Summary:" so readers can tell the AI text apart later:
  //   AI Summary: <file summary> (filename).
  //   AI Summary: <file summary> (filename).
  //   AI Summary: <photo summary> (All Photos Attached)
  _composeAiBlock({ auto }) {
    const paragraphs = []
    if (this._fileSummaryText) {
      this._fileSummaryText.split(/\n+/).forEach(p => {
        const text = p.trim()
        if (text) paragraphs.push(text)
      })
    }
    if (this._photoSummaryText) {
      paragraphs.push(`${this._photoSummaryText.replace(/[\s.]+$/, "")} (All Photos Attached)`)
    }
    if (!paragraphs.length) return false
    const block = paragraphs
      .map(p => (p.startsWith("AI Summary") ? p : `AI Summary: ${p}`))
      .join("\n\n")
    return this._insertAiSummary(block, { auto })
  }

  _schedulePhotoSummary() {
    clearTimeout(this._photoSumTimer)
    this._photoSumTimer = setTimeout(() => this._runPhotoSummary(), 1500)
  }

  async _runPhotoSummary() {
    if (!this._photoAiFiles.length || !this.photoAiUrlValue) return

    // One request at a time; if photos land mid-run, queue one refresh.
    if (this._photoSummarizing) {
      this._photoSummaryPending = true
      return
    }
    this._photoSummarizing = true

    const start = performance.now()
    clearInterval(this._photoSumTicker)
    const tick = () => {
      const secs = Math.round((performance.now() - start) / 1000)
      this._setSummaryStatus(`<span class="font-bold ai-rainbow-text">ConsLog AI — Reading the photos...</span> <span class="text-gray-500 tabular-nums">${secs}s</span>`)
    }
    tick()
    this._photoSumTicker = setInterval(tick, 1000)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.photoAiUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ files: this._photoAiFiles })
      })
      const data = await res.json().catch(() => ({}))
      clearInterval(this._photoSumTicker)
      if (!res.ok || data.error || !data.summary) {
        this._setSummaryStatus(`<span class="text-red-600">${data.error || "Photo summary failed — it will retry with the next photo."}</span>`)
        return
      }
      this._photoSummaryText = data.summary
      const inserted = this._composeAiBlock({ auto: true })
      if (inserted) {
        this._setSummaryStatus(`<span class="text-green-600">AI summary added to the notes — edit as needed.</span>`)
      } else {
        this._revealSummarizeButton()
        this._setSummaryStatus(`<span class="text-gray-500">Photos read — click “Summarize files with AI” to update your notes.</span>`)
      }
      clearTimeout(this._photoSumDoneTimer)
      this._photoSumDoneTimer = setTimeout(() => { if (!this._photoSummarizing && !this._summarizing) this._setSummaryStatus("") }, 6000)
    } catch (e) {
      clearInterval(this._photoSumTicker)
      this._setSummaryStatus(`<span class="text-red-600">Photo summary failed — check your connection.</span>`)
    } finally {
      this._photoSummarizing = false
      if (this._photoSummaryPending) {
        this._photoSummaryPending = false
        this._runPhotoSummary()
      }
    }
  }

  // AI-drafted comment for one uploaded photo — the construction work being
  // performed, in the user's own language. Fills the photo's comment input
  // only while the user hasn't typed anything; stays fully editable.
  async _aiPhotoComment(index, entry, registered) {
    if (!this.hasPhotoCaptionUrlValue || !this.photoCaptionUrlValue) return
    const input = this.photoListTarget?.querySelector(`input[data-upload-photo-index="${index}"]`)
    if (!input || input.value.trim()) return

    // Animated shimmer overlay ("AI is working here") — gradient text can't
    // style a placeholder, so float it over the input; clicks pass through
    // and the first keystroke removes it.
    const originalPlaceholder = input.placeholder
    input.placeholder = ""
    const wrap = document.createElement("div")
    wrap.style.position = "relative"
    input.parentNode.insertBefore(wrap, input)
    wrap.appendChild(input)
    const overlay = document.createElement("span")
    overlay.className = "ai-rainbow-text"
    overlay.textContent = "ConsLog AI is writing a comment…"
    overlay.style.cssText = "position:absolute;left:9px;top:50%;transform:translateY(-50%);font-size:13px;font-weight:600;pointer-events:none;white-space:nowrap;overflow:hidden;max-width:calc(100% - 18px);"
    wrap.appendChild(overlay)
    input.addEventListener("input", () => overlay.remove(), { once: true })
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.photoCaptionUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          cache_name: entry._uploadResult.cache_name,
          filename: entry._uploadResult.filename,
          attachment_id: registered.id
        })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.error || !data.comment) return

      // Only fill if the user still hasn't typed their own comment.
      if (!input.value.trim()) {
        input.value = data.comment
        if (this._uploadPhotos[index]) this._uploadPhotos[index].comment = data.comment
        this._photoComments[registered.photo_id] = data.comment
        this._syncHiddenFields()
      }
    } catch (e) {
      // Silent — the comment box just stays empty for the user to fill.
    } finally {
      overlay.remove()
      input.placeholder = originalPlaceholder
    }
  }

  async _doFileUpload(entries) {
    if (!this.hasFileListTarget) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    this._startUpload()

    // Show uploading placeholders
    const placeholders = entries.map(({ entry }) => {
      const chip = this._createFileChip(entry.file.name, { uploading: true })
      this.fileListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: entries.map(({ entry }) => ({
            filename: entry.file.name,
            content_type: entry.file.type || "application/octet-stream",
            byte_size: entry.file.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const file = entries[i].entry.file
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": file.type || upload.content_type },
            body: file
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results.filter(r => r.status === "fulfilled").map(r => r.value)
      if (successful.length === 0) throw new Error("All uploads failed")

      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      const registered = await registerRes.json()

      // Track for "Summarize files with AI" (testing/inspection reports).
      successful.forEach((u, i) => this._trackAiFile(u.cache_name, u.filename, u.content_type, registered[i]?.id))

      // Replace placeholders with final chips
      placeholders.forEach(p => p.remove())
      registered.forEach((a, i) => {
        const name = a.filename || `File #${a.id}`
        const cacheName = successful[i]?.cache_name
        this._fileIds.push(a.photo_id)

        const chip = this._createFileChip(name, {
          removable: true,
          onRemove: () => {
            chip.remove()
            this._fileIds = this._fileIds.filter(id => id !== a.photo_id)
            this._untrackAiFile(cacheName)
            this._syncHiddenFields()
          }
        })
        this.fileListTarget.appendChild(chip)
      })

      this._syncHiddenFields()
    } catch (e) {
      console.error("File upload failed:", e)
      placeholders.forEach(p => p.remove())
      // Say which files didn't make it instead of their chips just vanishing.
      if (!this._disconnected) {
        const template = this.i18nVideoUploadFailedValue || "__NAME__ didn't upload. Please try again."
        entries.forEach(({ entry }) => showToast(template.replace("__NAME__", entry.file.name), "error"))
      }
    } finally {
      this._finishUpload()
    }
  }

  // ── Upload state (buttons + status indicator) ──────────────────────

  _startUpload() {
    this._pendingUploads++
    this._updateUploadUI()
  }

  _finishUpload() {
    this._pendingUploads--
    this._updateUploadUI()
  }

  _updateUploadUI() {
    const uploading = this._pendingUploads > 0
    if (this.hasDraftBtnTarget) this.draftBtnTarget.disabled = uploading
    if (this.hasPublishBtnTarget) this.publishBtnTarget.disabled = uploading
    if (this.hasUploadStatusTarget) {
      this.uploadStatusTarget.classList.toggle("hidden", !uploading)
    }
    if (this.hasUploadStatusTextTarget && uploading) {
      const total = this._uploadPhotos.filter(Boolean).length + this._uploadFiles.filter(Boolean).length
      this.uploadStatusTextTarget.textContent = `Uploading ${total} file${total !== 1 ? "s" : ""}…`
    }
  }

  // ── Hidden fields sync ──────────────────────────────────────────────

  _syncHiddenFields() {
    if (!this.hasHiddenFieldsTarget) return

    let html = ""
    this._photoIds.forEach(id => {
      html += `<input type="hidden" name="photo_ids[]" value="${id}">`
    })
    this._fileIds.forEach(id => {
      html += `<input type="hidden" name="file_ids[]" value="${id}">`
    })
    Object.entries(this._photoComments).forEach(([id, comment]) => {
      const escaped = comment.replace(/"/g, "&quot;")
      html += `<input type="hidden" name="photo_comments[${id}]" value="${escaped}">`
    })
    this._removedIds.forEach(id => {
      html += `<input type="hidden" name="removed_photo_ids[]" value="${id}">`
    })
    // Rows flagged for the reviewer (form_data[table][row][__flag]).
    Object.entries(this._rowFlags).forEach(([tk, rows]) => {
      Object.entries(rows).forEach(([rk, on]) => {
        if (on) html += `<input type="hidden" name="form_data[${this._escAttr(tk)}][${this._escAttr(rk)}][__flag]" value="1">`
      })
    })
    // Which grid row each photo belongs to (form_data[table][row][__photos][]).
    Object.entries(this._rowPhotos).forEach(([tk, rows]) => {
      Object.entries(rows).forEach(([rk, ids]) => {
        ids.forEach(id => { html += `<input type="hidden" name="form_data[${this._escAttr(tk)}][${this._escAttr(rk)}][__photos][]" value="${id}">` })
      })
    })
    Object.entries(this._photoTasks).forEach(([photoId, data]) => {
      if (data.task_id) {
        html += `<input type="hidden" name="photo_tasks[${photoId}][task_id]" value="${data.task_id}">`
        html += `<input type="hidden" name="photo_tasks[${photoId}][percent_complete]" value="${data.percent_complete || 0}">`
      }
    })
    this.hiddenFieldsTarget.innerHTML = html
  }

  // ── HEIC handling ───────────────────────────────────────────────────

  _isHeic(file) {
    const type = file.type.toLowerCase()
    if (type.includes("heic") || type.includes("heif")) return true
    const ext = file.name.split(".").pop().toLowerCase()
    return ext === "heic" || ext === "heif"
  }

  async _convertHeic(file, index, entry) {
    const row = this.photoListTarget?.querySelector(`[data-upload-photo-index="${index}"]`)
    const thumbDiv = row?.querySelector("[data-role='upload-thumb']")

    try {
      if (!this._heicWorker) {
        this._heicWorker = new Worker(this.heicWorkerUrlValue)
        this._heicCallbacks = {}
        this._heicWorker.onmessage = (e) => {
          const cb = this._heicCallbacks[e.data.id]
          if (cb) {
            delete this._heicCallbacks[e.data.id]
            if (e.data.error) cb.reject(new Error(e.data.error))
            else cb.resolve(e.data)
          }
        }
        this._heicWorker.onerror = (e) => {
          console.error("HEIC worker error:", e)
          for (const [cbId, cb] of Object.entries(this._heicCallbacks)) {
            cb.reject(new Error("Worker error"))
            delete this._heicCallbacks[cbId]
          }
        }
      }

      const buffer = await file.arrayBuffer()
      const id = `heic_${Date.now()}_${index}`

      const result = await new Promise((resolve, reject) => {
        this._heicCallbacks[id] = { resolve, reject }
        this._heicWorker.postMessage({ id, buffer }, [buffer])
      })

      const canvas = document.createElement("canvas")
      canvas.width = result.width
      canvas.height = result.height
      const ctx = canvas.getContext("2d")
      const imageData = new ImageData(
        new Uint8ClampedArray(result.data),
        result.width,
        result.height
      )
      ctx.putImageData(imageData, 0, 0)

      const converted = await new Promise(resolve => {
        canvas.toBlob(blob => resolve(blob), "image/jpeg", 0.8)
      })

      entry.convertedBlob = converted

      if (thumbDiv) {
        const blobUrl = URL.createObjectURL(converted)
        thumbDiv.innerHTML = `<img src="${blobUrl}" class="w-full h-full object-cover">`
      }
    } catch (e) {
      console.warn("HEIC conversion failed, server will handle it:", e)
      if (thumbDiv) {
        thumbDiv.innerHTML = this._imagePlaceholderSvg()
      }
    }
  }

  // ── Compression ─────────────────────────────────────────────────────

  async _prepareFile(entry) {
    if (entry._heicPromise) await entry._heicPromise
    const file = entry.file
    const source = entry.convertedBlob || file
    if (!this._isCompressibleImage(source)) {
      return { name: file.name, blob: source }
    }
    const blob = await this._compressImage(source)
    return { name: file.name, blob }
  }

  _isCompressibleImage(file) {
    return /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type)
  }

  _compressImage(file, maxDim = 2000, quality = 0.80) {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        let { width, height } = img

        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(
          (blob) => resolve(blob || file),
          "image/jpeg",
          quality
        )
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        resolve(file)
      }
      img.src = URL.createObjectURL(file)
    })
  }

  // ── Thumbnail polling ───────────────────────────────────────────────

  _pollForThumbnails(photoIds) {
    if (!this.pollThumbnailsUrlValue || !photoIds.length) return

    let pending = new Set(photoIds)
    let attempts = 0
    const maxAttempts = 25

    const poll = async () => {
      if (pending.size === 0 || attempts >= maxAttempts) return
      attempts++

      try {
        const url = new URL(this.pollThumbnailsUrlValue, window.location.origin)
        pending.forEach(id => url.searchParams.append("ids[]", id))

        const res = await fetch(url, { headers: { "Accept": "application/json" } })
        if (!res.ok) {
          if (pending.size > 0) setTimeout(poll, 3000)
          return
        }

        const data = await res.json()
        const photos = data.photos || {}

        for (const [idStr, info] of Object.entries(photos)) {
          if (!info.ready) continue
          const id = parseInt(idStr)

          const entryIdx = this._uploadPhotos.findIndex(
            e => e?._uploadResult?.photo_id === id
          )
          if (entryIdx >= 0 && info.thumbnail_url) {
            const row = this.photoListTarget?.querySelector(
              `[data-upload-photo-index="${entryIdx}"]`
            )
            const thumbDiv = row?.querySelector("[data-role='upload-thumb']")
            if (thumbDiv) {
              const img = new Image()
              img.src = info.thumbnail_url
              img.onload = () => {
                thumbDiv.innerHTML = `<img src="${info.thumbnail_url}" class="w-full h-full object-cover">`
              }
            }
          }

          pending.delete(id)
        }
      } catch (e) {
        console.error("Thumbnail poll failed:", e)
      }

      if (pending.size > 0) {
        setTimeout(poll, attempts <= 3 ? 2000 : 3000)
      }
    }

    setTimeout(poll, 2000)
  }

  // ── File chip (matches real estate steps pattern) ───────────────────

  _createFileChip(name, { removable = false, uploading = false, onRemove = null, video = false } = {}) {
    const chip = document.createElement("div")
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs`
    chip.classList.add(uploading ? "chip-uploading" : "chip-file")
    if (video) chip.dataset.videoChip = ""

    // A video's chip carries a camera instead of the page (K9-6).
    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : video
        ? `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"/></svg>`
        : `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${name}">${truncatedName}</span>${removeBtn}`

    if (removable && onRemove) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }

  // ── SVG helpers ─────────────────────────────────────────────────────

  _spinnerSvg() {
    return `<svg class="w-5 h-5 text-gray-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _imagePlaceholderSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"/></svg>`
  }

  _xSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>`
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // ── Workforce rows ──────────────────────────────────────────────────

  addWorkforceRow() {
    const html = `
      <div class="flex gap-2 items-center" data-report-form-target="workforceRow">
        <div class="flex-1 relative">
          <input type="text" name="workforce_items[][description]"
                 autocomplete="off"
                 class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
        </div>
        <div class="w-24">
          <input type="number" name="workforce_items[][direct]"
                 min="0" placeholder="0"
                 data-action="input->report-form#updateWorkforceTotals"
                 class="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none">
        </div>
        <div class="w-24">
          <input type="number" name="workforce_items[][indirect]"
                 min="0" placeholder="0"
                 data-action="input->report-form#updateWorkforceTotals"
                 class="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none">
        </div>
        <div class="w-7 flex justify-end">
          <button type="button" data-action="click->report-form#removeRow"
                  class="p-1 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
    `
    this.workforceRowsTarget.insertAdjacentHTML("beforeend", html)
    const rows = this.workforceRowsTarget.querySelectorAll("[data-report-form-target='workforceRow']")
    const lastRow = rows[rows.length - 1]
    const input = lastRow?.querySelector("input[type='text']")
    if (input) {
      this._attachAutocomplete(input, "workforce")
      input.focus()
    }
  }

  removeRow(e) {
    const row = e.currentTarget.closest("[data-report-form-target]")
    if (row) {
      row.remove()
      this.updateWorkforceTotals()
      this.updateEquipmentTotals()
    }
  }

  updateWorkforceTotals() {
    let directTotal = 0
    let indirectTotal = 0
    this.workforceRowsTarget.querySelectorAll("[data-report-form-target='workforceRow']").forEach(row => {
      const inputs = row.querySelectorAll("input[type='number']")
      directTotal += parseInt(inputs[0]?.value) || 0
      indirectTotal += parseInt(inputs[1]?.value) || 0
    })
    if (this.hasWorkforceDirectTotalTarget) this.workforceDirectTotalTarget.textContent = directTotal
    if (this.hasWorkforceIndirectTotalTarget) this.workforceIndirectTotalTarget.textContent = indirectTotal
  }

  // ── Equipment rows ──────────────────────────────────────────────────

  addEquipmentRow() {
    const html = `
      <div class="flex gap-2 items-center" data-report-form-target="equipmentRow">
        <div class="flex-1 relative">
          <input type="text" name="equipment_items[][description]"
                 autocomplete="off"
                 class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">
        </div>
        <div class="w-24">
          <input type="number" name="equipment_items[][own]"
                 min="0" placeholder="0"
                 data-action="input->report-form#updateEquipmentTotals"
                 class="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none">
        </div>
        <div class="w-24">
          <input type="number" name="equipment_items[][third_party]"
                 min="0" placeholder="0"
                 data-action="input->report-form#updateEquipmentTotals"
                 class="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none">
        </div>
        <div class="w-7 flex justify-end">
          <button type="button" data-action="click->report-form#removeRow"
                  class="p-1 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
    `
    this.equipmentRowsTarget.insertAdjacentHTML("beforeend", html)
    const rows = this.equipmentRowsTarget.querySelectorAll("[data-report-form-target='equipmentRow']")
    const lastRow = rows[rows.length - 1]
    const input = lastRow?.querySelector("input[type='text']")
    if (input) {
      this._attachAutocomplete(input, "equipment")
      input.focus()
    }
  }

  updateEquipmentTotals() {
    let ownTotal = 0
    let thirdPartyTotal = 0
    this.equipmentRowsTarget.querySelectorAll("[data-report-form-target='equipmentRow']").forEach(row => {
      const inputs = row.querySelectorAll("input[type='number']")
      ownTotal += parseInt(inputs[0]?.value) || 0
      thirdPartyTotal += parseInt(inputs[1]?.value) || 0
    })
    if (this.hasEquipmentOwnTotalTarget) this.equipmentOwnTotalTarget.textContent = ownTotal
    if (this.hasEquipmentThirdPartyTotalTarget) this.equipmentThirdPartyTotalTarget.textContent = thirdPartyTotal
  }

  // ── Description autocomplete ─────────────────────────────────────────

  _initDescriptionAutocompletes() {
    // Attach autocomplete to existing workforce description inputs
    if (this.hasWorkforceRowsTarget) {
      this.workforceRowsTarget.querySelectorAll("input[name='workforce_items[][description]']").forEach(input => {
        this._attachAutocomplete(input, "workforce")
      })
    }
    // Attach autocomplete to existing equipment description inputs
    if (this.hasEquipmentRowsTarget) {
      this.equipmentRowsTarget.querySelectorAll("input[name='equipment_items[][description]']").forEach(input => {
        this._attachAutocomplete(input, "equipment")
      })
    }
  }

  _attachAutocomplete(input, type) {
    const wrapper = input.parentElement
    if (!wrapper.classList.contains("relative")) wrapper.classList.add("relative")

    input.setAttribute("autocomplete", "off")

    input.addEventListener("focus", () => this._showAutocomplete(input, type))
    input.addEventListener("input", () => this._showAutocomplete(input, type))
    input.addEventListener("keydown", (e) => this._handleAutocompleteKeydown(e, input, type))
  }

  _getSuggestions(type) {
    return type === "workforce" ? this.workforceSuggestionsValue : this.equipmentSuggestionsValue
  }

  _showAutocomplete(input, type) {
    const suggestions = this._getSuggestions(type)
    const q = input.value.toLowerCase().trim()
    const filtered = q
      ? suggestions.filter(s => s.toLowerCase().includes(q))
      : suggestions

    const exactMatch = q && suggestions.some(s => s.toLowerCase() === q)
    const showAddNew = q && !exactMatch

    if (!filtered.length && !showAddNew) {
      this._removeDropdown(input)
      return
    }

    let dropdown = input.parentElement.querySelector(".js-ac-dropdown")
    if (!dropdown) {
      dropdown = document.createElement("div")
      dropdown.className = "js-ac-dropdown absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
      input.parentElement.appendChild(dropdown)
    }

    dropdown.innerHTML = ""
    this._activeAutocomplete = dropdown

    filtered.forEach((suggestion, i) => {
      const row = document.createElement("div")
      row.className = "flex items-center hover:bg-brand/10 group"
      if (i === 0) row.classList.add("js-ac-active")

      const item = document.createElement("button")
      item.type = "button"
      item.className = "flex-1 text-left px-3 py-1.5 text-sm text-gray-700 group-[.js-ac-active]:text-brand cursor-pointer"
      item.textContent = suggestion
      item.addEventListener("mousedown", (e) => {
        e.preventDefault()
        input.value = suggestion
        this._removeDropdown(input)
        const r = input.closest("[data-report-form-target]")
        r?.querySelector("input[type='number']")?.focus()
      })

      const trashBtn = document.createElement("button")
      trashBtn.type = "button"
      trashBtn.className = "shrink-0 p-1 mr-1.5 text-gray-300 hover:text-red-500 cursor-pointer rounded transition-colors"
      trashBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>'
      trashBtn.addEventListener("mousedown", (e) => {
        e.preventDefault()
        e.stopPropagation()
        this._deleteSuggestion(input, type, suggestion)
      })

      row.appendChild(item)
      row.appendChild(trashBtn)
      dropdown.appendChild(row)
    })

    // "+ Save as new" option when typed value doesn't match any existing suggestion
    if (showAddNew) {
      if (filtered.length) {
        const divider = document.createElement("div")
        divider.className = "border-t border-gray-100"
        dropdown.appendChild(divider)
      }
      const addBtn = document.createElement("button")
      addBtn.type = "button"
      addBtn.className = "js-ac-add-new w-full text-left px-3 py-1.5 text-sm text-brand font-medium hover:bg-brand/10 cursor-pointer flex items-center gap-1.5"
      addBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg> Save "${this._escapeForHtml(input.value.trim())}"`
      if (!filtered.length) addBtn.classList.add("js-ac-active")
      addBtn.addEventListener("mousedown", (e) => {
        e.preventDefault()
        this._saveNewSuggestion(input, type)
      })
      dropdown.appendChild(addBtn)
    }
  }

  _escapeForHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  async _saveNewSuggestion(input, type) {
    const value = input.value.trim()
    if (!value) return

    const url = type === "workforce" ? this.newWorkforceNameUrlValue : this.newEquipmentNameUrlValue
    const paramKey = type === "workforce" ? "new_workforce_name" : "new_equipment_name"

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ [paramKey]: value })
      })

      if (resp.ok) {
        if (type === "workforce") {
          this.workforceSuggestionsValue = [...this.workforceSuggestionsValue, value]
        } else {
          this.equipmentSuggestionsValue = [...this.equipmentSuggestionsValue, value]
        }
        this._removeDropdown(input)
        this._showToast(`"${value}" saved`)
        const row = input.closest("[data-report-form-target]")
        row?.querySelector("input[type='number']")?.focus()
      }
    } catch (_) {
      // silently fail — user can retry
    }
  }

  async _deleteSuggestion(input, type, description) {
    const url = type === "workforce" ? this.deleteWorkforceNameUrlValue : this.deleteEquipmentNameUrlValue

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const resp = await fetch(url, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ description })
      })

      if (resp.ok) {
        if (type === "workforce") {
          this.workforceSuggestionsValue = this.workforceSuggestionsValue.filter(s => s !== description)
        } else {
          this.equipmentSuggestionsValue = this.equipmentSuggestionsValue.filter(s => s !== description)
        }
        this._showToast(`"${description}" removed`)
        // Re-render the dropdown with updated suggestions
        this._showAutocomplete(input, type)
      }
    } catch (_) {
      // silently fail — user can retry
    }
  }

  _handleAutocompleteKeydown(e, input, type) {
    const dropdown = input.parentElement.querySelector(".js-ac-dropdown")
    if (!dropdown) return

    // Navigable items: suggestion rows (div.flex) and the add-new button
    const items = dropdown.querySelectorAll(".js-ac-active, div.flex.items-center, button.js-ac-add-new")
    const navigable = Array.from(new Set(items.length ? items : dropdown.querySelectorAll(":scope > div.flex, :scope > button.js-ac-add-new")))
    if (!navigable.length) return

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      let idx = navigable.findIndex(el => el.classList.contains("js-ac-active"))
      navigable[idx]?.classList.remove("js-ac-active")
      if (e.key === "ArrowDown") idx = (idx + 1) % navigable.length
      else idx = (idx - 1 + navigable.length) % navigable.length
      navigable[idx].classList.add("js-ac-active")
      navigable[idx].scrollIntoView({ block: "nearest" })
    } else if (e.key === "Enter") {
      const active = dropdown.querySelector(".js-ac-active")
      if (active) {
        e.preventDefault()
        if (active.classList.contains("js-ac-add-new")) {
          this._saveNewSuggestion(input, type)
        } else {
          // It's a suggestion row div — get the text from its button child
          const textBtn = active.querySelector("button") || active
          input.value = textBtn.textContent
          this._removeDropdown(input)
          const row = input.closest("[data-report-form-target]")
          row?.querySelector("input[type='number']")?.focus()
        }
      }
    } else if (e.key === "Escape") {
      this._removeDropdown(input)
    }
  }

  _removeDropdown(input) {
    const dropdown = input.parentElement.querySelector(".js-ac-dropdown")
    if (dropdown) dropdown.remove()
    if (this._activeAutocomplete === dropdown) this._activeAutocomplete = null
  }

  _closeAutocompleteOnOutsideClick(e) {
    if (!this._activeAutocomplete) return
    if (!this._activeAutocomplete.parentElement?.contains(e.target)) {
      this._activeAutocomplete.remove()
      this._activeAutocomplete = null
    }
  }

  // ── Geolocation ──────────────────────────────────────────────────────

  _checkGeolocation() {
    if (!navigator.geolocation) return
    if (this.projectLatitudeValue === 0 && this.projectLongitudeValue === 0) return

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (this._disconnected) return
        const userLat = position.coords.latitude
        const userLng = position.coords.longitude

        const distance = this._haversineDistance(
          userLat, userLng,
          this.projectLatitudeValue, this.projectLongitudeValue
        )

        if (distance < 1000) {
          if (this.hasLatitudeTarget) this.latitudeTarget.value = userLat
          if (this.hasLongitudeTarget) this.longitudeTarget.value = userLng
          this._showLocationToast(this.locationVerifiedMsgValue || "You are at the job site.")
        } else {
          if (this.hasLatitudeTarget) this.latitudeTarget.value = ""
          if (this.hasLongitudeTarget) this.longitudeTarget.value = ""
          this._showLocationToast(this.locationWarningMsgValue || "You are not at the jobsite. This will be noted on the report.", "warning")
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  }

  _haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000 // Earth radius in meters
    const toRad = (deg) => deg * Math.PI / 180
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }

  _showLocationToast(message, type = "success") {
    const pinIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" stroke="currentColor" stroke-width="32" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5"><path d="M256 48c-79.5 0-144 61.39-144 137 0 87 96 224.87 131.25 272.49a15.77 15.77 0 0025.5 0C304 409.89 400 272.07 400 185c0-75.61-64.5-137-144-137z"/><circle cx="256" cy="192" r="48"/></svg>'
    const el = document.createElement("div")
    el.className = type === "warning" ? "flash-toast-warning" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "6000")
    el.setAttribute("data-turbo-temporary", "")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = pinIcon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _showToast(message) {
    const el = document.createElement("div")
    el.className = "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "3000")
    el.setAttribute("data-turbo-temporary", "")
    el.setAttribute("role", "alert")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── Photo-schedule integration ──────────────────────────────────────

  async _fetchScheduleTasks() {
    try {
      const res = await fetch(this.scheduleTasksUrlValue, { headers: { "Accept": "application/json" } })
      if (!res.ok) return
      this._scheduleTasks = await res.json()
    } catch (e) {
      console.error("Failed to fetch schedule tasks:", e)
    }
  }

  async _renderActivityLink(photoId, container, existingTaskId = null, existingPercent = null) {
    if (!this._photoScheduleEnabled) return

    const wrapper = document.createElement("div")
    wrapper.className = "mt-1.5"
    wrapper.dataset.activityWrapper = photoId
    container.appendChild(wrapper)

    // Wait for tasks to load before rendering the UI
    await this._scheduleTasksPromise

    if (existingTaskId) {
      this._photoTasks[photoId] = { task_id: existingTaskId, percent_complete: existingPercent || 0 }
      this._renderActivityPanel(photoId, wrapper, existingTaskId, existingPercent)
      this._syncHiddenFields()
    } else {
      this._renderActivityToggle(photoId, wrapper)
    }
  }

  _renderActivityToggle(photoId, wrapper) {
    wrapper.innerHTML = ""
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "inline-flex items-center gap-1 text-[13px] text-brand hover:text-brand/80 cursor-pointer"
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/></svg> ${this._escHtml(this.i18nLinkActivityValue || "Link to activity")}`
    btn.addEventListener("click", () => {
      this._renderActivityPanel(photoId, wrapper)
    })
    wrapper.appendChild(btn)
  }

  _renderActivityPanel(photoId, wrapper, preselectedTaskId = null, preselectedPercent = null) {
    wrapper.innerHTML = ""
    const panel = document.createElement("div")
    panel.className = "bg-blue-50/60 border border-blue-200 rounded-lg p-2"

    // Task selector row (searchable popover)
    const selectRow = document.createElement("div")
    selectRow.className = "flex items-center gap-2"

    const selectorWrap = document.createElement("div")
    selectorWrap.className = "flex-1 relative"

    // Readonly display input
    const display = document.createElement("input")
    display.type = "text"
    display.readOnly = true
    const preselectedTask = preselectedTaskId ? this._scheduleTasks.find(t => t.id === preselectedTaskId) : null
    display.value = preselectedTask ? preselectedTask.name : ""
    display.placeholder = this.i18nSelectActivityValue || "Select activity..."
    display.className = `w-full px-2 py-1 pr-7 text-[13px] border border-gray-200 rounded bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand truncate ${preselectedTask ? "text-gray-900" : "text-gray-400"}`

    // Chevron icon
    const chevron = document.createElement("svg")
    chevron.setAttribute("class", "w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none")
    chevron.setAttribute("fill", "none")
    chevron.setAttribute("viewBox", "0 0 24 24")
    chevron.setAttribute("stroke-width", "1.5")
    chevron.setAttribute("stroke", "currentColor")
    chevron.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>`

    // Hidden value holder
    let selectedTaskId = preselectedTaskId || null

    // Popover
    const popover = document.createElement("div")
    popover.className = "hidden absolute left-0 right-0 z-30 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg"
    popover.style.minWidth = "240px"

    const searchWrap = document.createElement("div")
    searchWrap.className = "p-2 border-b border-gray-100"
    const searchInput = document.createElement("input")
    searchInput.type = "text"
    searchInput.placeholder = this.i18nSearchValue || "Search..."
    searchInput.className = "w-full px-2 py-1 text-[13px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
    searchWrap.appendChild(searchInput)
    popover.appendChild(searchWrap)

    const listDiv = document.createElement("div")
    listDiv.className = "max-h-48 overflow-y-auto"
    popover.appendChild(listDiv)

    selectorWrap.appendChild(display)
    selectorWrap.appendChild(chevron)
    selectorWrap.appendChild(popover)
    selectRow.appendChild(selectorWrap)

    // Render filtered list
    const renderList = (query) => {
      const q = query.toLowerCase()
      const filtered = q
        ? this._scheduleTasks.filter(t => t.name.toLowerCase().includes(q))
        : this._scheduleTasks

      if (filtered.length === 0) {
        listDiv.innerHTML = `<div class="px-3 py-4 text-xs text-gray-400 text-center">${q ? (this.i18nNoMatchesValue || "No matches") : (this.i18nNoActivitiesValue || "No activities")}</div>`
        return
      }

      listDiv.innerHTML = ""
      filtered.forEach(t => {
        const item = document.createElement("div")
        const isSelected = t.id === selectedTaskId
        item.className = `px-3 py-2 text-[13px] cursor-pointer transition-colors hover:bg-gray-50 ${isSelected ? "bg-brand/5 text-brand font-medium" : "text-gray-700"}`
        item.textContent = t.name
        item.addEventListener("click", () => {
          selectedTaskId = t.id
          display.value = t.name
          display.classList.remove("text-gray-400")
          display.classList.add("text-gray-900")
          popover.classList.add("hidden")
          onSelect(t.id)
        })
        listDiv.appendChild(item)
      })
    }

    // Open/close popover
    display.addEventListener("click", (e) => {
      e.stopPropagation()
      const isOpen = !popover.classList.contains("hidden")
      if (isOpen) {
        popover.classList.add("hidden")
      } else {
        searchInput.value = ""
        renderList("")
        popover.classList.remove("hidden")
        searchInput.focus()
      }
    })

    searchInput.addEventListener("input", () => renderList(searchInput.value.trim()))
    searchInput.addEventListener("click", (e) => e.stopPropagation())

    // Close popover on outside click
    const closeOnOutside = (e) => {
      if (!selectorWrap.contains(e.target)) {
        popover.classList.add("hidden")
      }
    }
    document.addEventListener("click", closeOnOutside)
    // Cleanup when panel is removed
    const observer = new MutationObserver(() => {
      if (!wrapper.contains(panel)) {
        document.removeEventListener("click", closeOnOutside)
        observer.disconnect()
      }
    })
    observer.observe(wrapper, { childList: true })

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "p-1 text-gray-400 hover:text-red-500 shrink-0 cursor-pointer"
    removeBtn.innerHTML = this._xSvg()
    removeBtn.addEventListener("click", () => {
      delete this._photoTasks[photoId]
      this._renderActivityToggle(photoId, wrapper)
      this._syncHiddenFields()
    })
    selectRow.appendChild(removeBtn)
    panel.appendChild(selectRow)

    // Progress row (shown when a task is selected)
    const progressRow = document.createElement("div")
    progressRow.className = "hidden mt-2"
    panel.appendChild(progressRow)

    const renderProgress = (taskId) => {
      if (!taskId) {
        progressRow.classList.add("hidden")
        return
      }
      const task = this._scheduleTasks.find(t => t.id === parseInt(taskId))
      if (!task) { progressRow.classList.add("hidden"); return }

      const existingPercent = this._photoTasks[photoId]?.percent_complete || preselectedPercent || 0

      progressRow.classList.remove("hidden")
      progressRow.innerHTML = `
        <div class="flex items-center gap-3 text-[13px]">
          <div class="flex items-center gap-1 text-gray-500">
            <span>${this._escHtml(this.i18nCurrentValue || "Current")}:</span>
            <span class="font-medium text-gray-700">${task.percent_done}%</span>
          </div>
          <div class="flex items-center gap-1 text-gray-500">
            <span>+</span>
            <input type="number" min="0" max="100" step="1" value="${existingPercent || ''}"
                   placeholder="0"
                   class="w-16 px-1.5 py-0.5 text-[13px] text-center border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                   data-percent-input="true">
            <span>%</span>
          </div>
        </div>
      `

      const percentInput = progressRow.querySelector("[data-percent-input]")
      percentInput.addEventListener("input", () => {
        let val = parseInt(percentInput.value) || 0
        if (val < 0) val = 0
        if (val > 100) val = 100
        this._photoTasks[photoId] = {
          task_id: parseInt(taskId),
          percent_complete: val
        }
        this._syncHiddenFields()
      })
    }

    const onSelect = (taskId) => {
      if (taskId) {
        this._photoTasks[photoId] = {
          task_id: parseInt(taskId),
          percent_complete: parseInt(progressRow.querySelector("[data-percent-input]")?.value) || 0
        }
      } else {
        delete this._photoTasks[photoId]
      }
      renderProgress(taskId)
      this._syncHiddenFields()
    }

    // If pre-selected, render progress immediately
    if (preselectedTaskId) {
      renderProgress(preselectedTaskId)
    }

    wrapper.appendChild(panel)
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}

applyMixin(ReportFormController, FormFieldRendererMixin)
applyMixin(ReportFormController, TemplateEditorMixin)

export default ReportFormController
